import React, { useState, useEffect, useRef } from 'react';
import { Camera, Mic, Heart, Play, Pause, Square, CheckCircle, AlertCircle, Activity, Shield, ShieldCheck, ShieldX, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import FaceDetectionOval from './FaceDetectionOval';
import RecordRTC from 'recordrtc';
import { vitalSignsAPI } from '../services/api/vitalSignsStorageAPI';
import { vitalSignsAnalysisAPI } from '../services/api/vitalSignsAnalysisAPI';
import toast from 'react-hot-toast';
import { recordingAPI } from '../services/api/api';

// Real-time Audio Visualizer Component
const RealTimeAudioVisualizer = ({ 
  isRecording, 
  barCount = 28, 
  barWidth = 8, 
  barGap = 1,
  containerHeight = 128 
}) => {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const animationFrameRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    if (!isRecording) {
      cleanup();
      return;
    }

    const initAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphoneRef.current = stream;

        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;

        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);

        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
        
        animate();
      } catch (error) {
        console.error('Audio initialization error:', error);
      }
    };

    initAudio();

    return () => {
      cleanup();
    };
  }, [isRecording]);

  const cleanup = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (microphoneRef.current) {
      microphoneRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
  };

  const animate = () => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Get frequency data
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Calculate bar dimensions for center-outward spread
    const totalBarWidth = barWidth + barGap;
    const centerX = width / 2;
    const halfBarCount = Math.floor(barCount / 2);
    
    // Draw bars from center outward
    for (let i = 0; i < barCount; i++) {
      // Calculate position relative to center
      let barX;
      if (i < halfBarCount) {
        // Left side of center
        barX = centerX - (halfBarCount - i) * totalBarWidth - barWidth / 2;
      } else {
        // Right side of center (including center bar)
        barX = centerX + (i - halfBarCount) * totalBarWidth - barWidth / 2;
      }
      
      const barIndex = Math.floor((i / (barCount - 1)) * (dataArrayRef.current.length - 1));
      const frequencyValue = dataArrayRef.current[barIndex] || 0;
      
      // Calculate bar height based on frequency data
      const barHeight = (frequencyValue / 255) * (height * 0.8);
      const minHeight = 3;
      const finalHeight = Math.max(minHeight, barHeight);

      // Calculate bar position (centered vertically)
      const barY = (height - finalHeight) / 2;

      // Create gradient from Vital Blue to Cyan Core
      const gradient = ctx.createLinearGradient(barX, barY + finalHeight, barX, barY);
      gradient.addColorStop(0, '#207CE5'); // Vital Blue at base
      gradient.addColorStop(1, '#00BCD4'); // Cyan Core at top

      // Draw bar
      ctx.fillStyle = gradient;
      ctx.fillRect(barX, barY, barWidth, finalHeight);

      // Add glow effect when audio is detected
      if (frequencyValue > 20) {
        ctx.shadowColor = '#00BCD4';
        ctx.shadowBlur = 8;
        ctx.fillRect(barX, barY, barWidth, finalHeight);
        ctx.shadowBlur = 0;
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  };

      return (
      <canvas
        ref={canvasRef}
        width={400}
        height={containerHeight}
        className="w-full h-full rounded"
        style={{ 
          background: '#ffffff'
        }}
      />
    );
};

// Custom Pulse Wave Visualizer Component
const PulseWaveVisualizer = ({ 
  isRecording, 
  audioLevel = 0,
  containerHeight = 200 
}) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!isRecording) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    const animate = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas with dark background
      ctx.fillStyle = '#0a0a2a';
      ctx.fillRect(0, 0, width, height);

      // Draw subtle grid lines
      ctx.strokeStyle = '#1a1a3a';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < width; x += 30) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 30) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // Get current time for animation
      const time = Date.now() * 0.001;
      
      // Calculate pulse rate based on audio level (60-100 BPM)
      const baseHeartRate = 72;
      const audioInfluence = (audioLevel / 255) * 20; // ±20 BPM variation
      const heartRate = Math.max(60, Math.min(100, baseHeartRate + audioInfluence));
      const beatInterval = 60 / heartRate;
      
      // Draw multiple pulse waves
      ctx.strokeStyle = '#00ff00'; // Green pulse waves
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw 3-4 complete pulse waves
      for (let waveIndex = 0; waveIndex < 4; waveIndex++) {
        ctx.beginPath();
        
        const waveOffset = waveIndex * beatInterval * 100; // Convert to pixels
        const waveStartX = -waveOffset + (time * 50) % (beatInterval * 100);
        
        for (let x = 0; x < width + 50; x += 2) {
          const waveX = x + waveStartX;
          const normalizedX = waveX / (beatInterval * 100);
          const beatPhase = (normalizedX % 1);
          
          // Create realistic pulse wave shape
          let pulseAmplitude = 0;
          
          if (beatPhase < 0.1) {
            // Initial rise (0-10%)
            const risePhase = beatPhase / 0.1;
            pulseAmplitude = Math.sin(risePhase * Math.PI) * height * 0.3;
          } else if (beatPhase < 0.2) {
            // Peak (10-20%)
            const peakPhase = (beatPhase - 0.1) / 0.1;
            pulseAmplitude = height * 0.3 * (1 - peakPhase * 0.3);
          } else if (beatPhase < 0.4) {
            // Decline (20-40%)
            const declinePhase = (beatPhase - 0.2) / 0.2;
            pulseAmplitude = height * 0.21 * (1 - declinePhase);
          } else if (beatPhase < 0.6) {
            // Dicrotic notch (40-60%)
            const notchPhase = (beatPhase - 0.4) / 0.2;
            pulseAmplitude = height * 0.15 * (1 - Math.sin(notchPhase * Math.PI) * 0.5);
          } else if (beatPhase < 0.8) {
            // Secondary wave (60-80%)
            const secondaryPhase = (beatPhase - 0.6) / 0.2;
            pulseAmplitude = height * 0.1 * Math.sin(secondaryPhase * Math.PI);
          } else {
            // Return to baseline (80-100%)
            pulseAmplitude = 0;
          }
          
          // Add audio level influence
          const audioInfluence = (audioLevel / 255) * height * 0.1;
          pulseAmplitude += audioInfluence;
          
          const y = height - pulseAmplitude - (height * 0.3);
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.stroke();
      }

      // Add heartbeat indicator dots
      const currentBeatPhase = (time % beatInterval) / beatInterval;
      if (currentBeatPhase < 0.1) {
        ctx.fillStyle = '#ff0000';
        ctx.beginPath();
        ctx.arc(width - 20, 20, 4, 0, 2 * Math.PI);
        ctx.fill();
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording, audioLevel]);

  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={containerHeight}
      className="w-full h-full rounded"
      style={{ 
        background: '#0a0a2a',
        filter: 'brightness(1.2) contrast(1.1)'
      }}
    />
  );
};

// Animated Zigzag Pulse Waveform Component
const AnimatedZigzagWaveform = ({ 
  isRecording, 
  audioLevel = 0,
  containerHeight = 200,
  color = 'rgb(100, 150, 200)', // Dull blue default
  speed = 1, // Animation speed multiplier
  barCount = 32 // Number of zigzag bars
}) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const dataArrayRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    if (!isRecording) {
      cleanup();
      return;
    }

    const initAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphoneRef.current = stream;

        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;

        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);

        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
        
        animate();
      } catch (error) {
        console.error('Audio initialization error:', error);
      }
    };

    initAudio();

    return () => {
      cleanup();
    };
  }, [isRecording]);

  const cleanup = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (microphoneRef.current) {
      microphoneRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
  };

  const animate = () => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Get frequency data
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Calculate average amplitude for silence detection
    const average = dataArrayRef.current.reduce((sum, value) => sum + value, 0) / dataArrayRef.current.length;
    const hasAudioInput = average > 8; // Silence threshold

    if (hasAudioInput) {
      // Update time for animation
      timeRef.current += 0.016 * speed;
      const time = timeRef.current;

      // Draw animated zigzag waveform
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.lineCap = 'square'; // Sharp edges for zigzag
      ctx.lineJoin = 'miter'; // Sharp corners
      ctx.beginPath();

      const centerY = height / 2;
      const barWidth = width / barCount;
      const maxHeight = height * 0.75; // Balanced amplitude - not too big, not too small

      // Create continuous left-to-right scrolling effect
      const scrollOffset = (time * 50) % (barWidth * 2); // Continuous scrolling
      
      for (let i = 0; i < barCount + 2; i++) {
        const barIndex = Math.floor((i / (barCount - 1)) * (dataArrayRef.current.length - 1));
        const frequencyValue = dataArrayRef.current[barIndex] || 0;
        
        // Calculate zigzag height based on frequency and animation (centered height)
        const baseHeight = (frequencyValue / 255) * maxHeight * 0.7; // Balanced amplitude - good movement but not overwhelming
        const animationOffset = Math.sin(time * 2 + i * 0.5) * 8; // Balanced animation offset for moderate movement
        const finalHeight = Math.max(2, baseHeight + animationOffset);

        const x = i * barWidth - scrollOffset;
        
        // Alternate between above and below center line
        const isAbove = i % 2 === 0;
        const y = centerY + (isAbove ? finalHeight : -finalHeight);

        // Draw zigzag pattern in center only
        if (i === 0) {
          ctx.moveTo(x, centerY);
        } else {
          // Draw diagonal line from center to peak (above or below)
          ctx.lineTo(x, y);
          // Draw diagonal line back to center for next segment
          ctx.lineTo(x + barWidth / 2, centerY);
        }
      }

      ctx.stroke();

      // Add glow effect
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.stroke();
      ctx.shadowBlur = 0;

    } else {
      // Show completely straight line when no audio
      ctx.strokeStyle = 'rgb(100, 150, 200)'; // Dull blue color
      ctx.lineWidth = 1;
      ctx.lineCap = 'square';
      ctx.lineJoin = 'miter';
      ctx.beginPath();

      const centerY = height / 2;
      
      // Draw completely straight horizontal line
      ctx.moveTo(0, centerY);
      ctx.lineTo(width, centerY);
      
      ctx.stroke();
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={containerHeight}
      className="w-full h-full rounded"
      style={{ 
        background: '#ffffff'
      }}
    />
  );
};

// Help Tooltip Component
const HelpTooltip = ({ currentStep }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const instructions = [
    {
      title: "Step 1",
      tips: [
        "Ensure your face is well-lit and clearly visible",
        "Keep your head and shoulders centered in the frame",
        "Stay still during the recording"
      ]
    },
    {
      title: "Step 2", 
      tips: [
        "Place the microphone slightly left of center on your chest or on your Carotid artery",
        "Remain still and quiet during the recording",
        "Avoid any background noise (TV, talking, fans, etc.)"
      ]
    },
    {
      title: "Step 3",
      tips: [
        "Hold the phone 6–8 inches from your mouth",
        "Speak in a steady, clear tone",
        "Avoid speaking too softly or too loudly"
      ]
    }
  ];

  // Get current step instructions
  const currentStepInstructions = instructions[currentStep];

  // Handle touch events for mobile
  const handleTouchStart = () => {
    setShowTooltip(true);
  };

  const handleTouchEnd = () => {
    // Small delay to allow users to read the tooltip
    setTimeout(() => setShowTooltip(false), 3000);
  };

  return (
    <div className="relative">
      {/* Help Icon */}
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="text-black-500 hover:text-black-600 transition-colors duration-200"
        aria-label="Help"
      >
        <Info className="h-5 w-5" />
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-10 right-0 z-50 w-40 sm:w-48 bg-white rounded-lg shadow-2xl border border-gray-200 p-1.5 sm:p-2 transform transition-all duration-200 ease-in-out">
          {/* Arrow */}
          <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
          
          {/* Header */}
          <div className="mb-1.5">
            <p className="text-xs text-gray-500">Tips</p>
          </div>

          {/* Current Step Instructions Only */}
          <div className="border border-gray-200 rounded p-1">
            {currentStep === 0 ? (
              <p className="text-xs text-gray-700 leading-tight">
                Hold your phone at eye level, centered, and record a 30-second video of yourself.
              </p>
            ) : currentStep === 1 ? (
              <p className="text-xs text-gray-700 leading-tight">
                Place your phone's microphone on your chest or throat. Stay still and quiet during data capture
              </p>
            ) : currentStep === 2 ? (
              <p className="text-xs text-gray-700 leading-tight">
                Hold your phone in front of your mouth and record 15 seconds of speech
              </p>
            ) : (
            <ul className="space-y-0">
              {currentStepInstructions.tips.map((tip, tipIndex) => (
                <li key={tipIndex} className="text-xs text-gray-700 flex items-start leading-tight mb-0.5">
                  <span className="text-blue-500   text-xs">•</span>
                  <span className="text-xs leading-tight">{tip}</span>
                </li>
              ))}
            </ul>
            )}
          </div>

          {/* Step Progress */}
          <div className="mt-1.5 pt-1 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Step:</span>
              <span className="text-xs font-medium text-blue-600">
                {currentStep + 1}/{instructions.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VitalSignsRecording = (props) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [frequencyData, setFrequencyData] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const [results, setResults] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0); // 🔧 BP FIX: Force re-render when BP changes
  const [processingStage, setProcessingStage] = useState('');
  const [pollingProgress, setPollingProgress] = useState(0);
  
  // 🎯 RECORDING FORMAT OPTIMIZATION: Platform detection for optimal format selection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  // 🚀 iPhone Chrome GUARANTEED SUCCESS: Precise browser detection
  // 🔧 FIXED: Enhanced iPhone Chrome detection for modern versions
  const isChromeOnIOS = /CriOS/i.test(navigator.userAgent) || 
                       (/iPhone|iPad|iPod/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent));
  const isSafariOnIOS = isIOS && !isChromeOnIOS;
    
  // 🎯 MOBILE POLLING OPTIMIZATION: Device and connection detection
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isSlowConnection = navigator.connection && navigator.connection.effectiveType && 
                          ['slow-2g', '2g', '3g'].includes(navigator.connection.effectiveType);
  
  // Permission states
  const [permissions, setPermissions] = useState({
    camera: null,
    microphone: null
  });
  const [permissionRequested, setPermissionRequested] = useState(false);
  
  // EKG waveform data
  const [ekgData, setEkgData] = useState([]);
  const [pulseRate, setPulseRate] = useState(0);
  
  // Vowel audio states
  const [vowelSegments, setVowelSegments] = useState([]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [segmentRecording, setSegmentRecording] = useState(false);
  const [segmentPaused, setSegmentPaused] = useState(false);
  const [vowelWaveform, setVowelWaveform] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // For step 2 image switching
  


  const [pulseDuration, setPulseDuration] = useState(30); // Default to 30 seconds
  const [videoDuration, setVideoDuration] = useState(30); // Default to 30 seconds

  // Storage-related state variables
  const [sessionId, setSessionId] = useState(null);
  const [storedRecordings, setStoredRecordings] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [recordingMetadata, setRecordingMetadata] = useState({
    video: null,
    pulse: null,
    vowel: null
  });

  const videoRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const frequencyCanvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  // ekgCanvasRef removed - now using video element
  const vowelCanvasRef = useRef(null);

  // Add interval refs for proper cleanup
  const pulseIntervalRef = useRef(null);
  const vowelIntervalRef = useRef(null);

  // 🎯 RECORDING FORMAT OPTIMIZATION: Get optimal MIME type based on device
  const getOptimalMimeType = (type) => {
    // 🚀 iPhone Chrome GUARANTEED SUCCESS: Special handling for Chrome on iOS
    if (isChromeOnIOS && type === 'video') {
      console.log('🔍 iPhone Chrome detected - using guaranteed MP4 strategy');
      
      const chromeFormats = [
        'video/mp4', // Basic MP4 - most likely to work
        'video/mp4;codecs=avc1.42E01E,mp4a.40.2', // H.264 Baseline
        'video/webm', // Last resort fallback
      ];
      
      for (const format of chromeFormats) {
        if (MediaRecorder.isTypeSupported(format)) {
          console.log(`🚀 iPhone Chrome selected format: ${format}`);
          return format;
        }
      }
      
      console.warn('⚠️ iPhone Chrome: No formats supported, using MP4 anyway');
      return 'video/mp4'; // Force MP4 even if not "supported"
    }
    
    // 🎯 Original logic for Safari and other platforms (unchanged)
    const formats = type === 'video' ? [
      ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', isSafariOnIOS], // Safari-specific
      ['video/mp4', isSafariOnIOS],
      ['video/webm;codecs=vp9,opus', isAndroid], // Android high-quality
      ['video/webm;codecs=vp8,opus', true], // Broad fallback
      ['video/webm', true],
    ] : [
      ['audio/wav', true], // Best quality, maximum compatibility
      ['audio/mp4;codecs=mp4a.40.2', isIOS], // iOS-optimized AAC-LC
      ['audio/mp4', isIOS],
      ['audio/webm;codecs=opus', true], // Android + fallback
      ['audio/webm', true],
    ];

    for (const [format, condition] of formats) {
      if ((condition === true || condition) && MediaRecorder.isTypeSupported(format)) {
        console.log(`🔍 Selected ${type} format: ${format}`);
        return format;
      }
    }
    return type === 'video' ? 'video/webm' : 'audio/webm';
  };

  // 🎯 RECORDING FORMAT OPTIMIZATION: Get RecordRTC config with device-optimized settings
  const getRecordRTCConfig = (type, mimeType) => {
    const baseConfig = { type, mimeType };
    
    // 🚀 iPhone Chrome OPTIMIZED: Updated settings for Phase 2
    if (isChromeOnIOS && type === 'video') {
      console.log('🔍 Using iPhone Chrome optimized recording config');
      
      // 🔍 LOGGER: iPhone Chrome config being applied
      const config = {
        ...baseConfig,
        // 🔧 REVERTED: iPhone Chrome optimized bitrates for 320x240 (bitrate control works)
        videoBitsPerSecond: mimeType.includes('mp4') ? 250000 : 350000,  // 250kbps for MP4 (proven to work)
        audioBitsPerSecond: mimeType.includes('mp4') ? 32000 : 48000,    // Keep 32kbps for MP4  
        frameInterval: 50,           // Keep 20 fps for algorithm
        timeSlice: 5000,            // Larger chunks
        quality: 0.7,               // Keep quality setting
        canvas: { width: 320, height: 240 },  // 🔧 REVERTED: 320x240 recording (works reliably)
        width: 320,                  // 🔧 REVERTED: Backup width setting
        height: 240,                 // 🔧 REVERTED: Backup height setting
        disableLogs: false,        // Enable logging for debugging
        recorderType: RecordRTC.MediaStreamRecorder,
      };
      
      console.log('🔍🔍🔍 IPHONE CHROME CONFIG APPLIED 🔍🔍🔍', config);
      return config;
    }
    
    // 🎯 PHASE 2: Updated mobile/desktop config with critical fixes
    const isMobile = isIOS || isAndroid;
    
    if (type === 'video') {
      // 🔍 LOGGER: Non-iPhone device config being applied  
      const config = {
        ...baseConfig,
        // 🎯 UPGRADED: Other mobile devices optimized bitrates for 640x480
        videoBitsPerSecond: mimeType.includes('mp4') ? 800000 : 900000,  // 800kbps for MP4 (as suggested)
        audioBitsPerSecond: mimeType.includes('mp4') ? 64000 : 128000,   // Keep existing audio bitrates
        frameInterval: 50,           // Keep 20 FPS for algorithm
        canvas: { width: 640, height: 480 },  // 🎯 UPGRADED: 640x480 recording
        width: 640,                  // 🎯 UPGRADED: Backup width setting
        height: 480,                 // 🎯 UPGRADED: Backup height setting
        quality: 0.7,                // Keep quality control
        timeSlice: 5000,            // Keep time slicing for better compression
      };
      
      console.log('🔍🔍🔍 NON-IPHONE CONFIG APPLIED 🔍🔍🔍', config);
      console.log('🔍 Device type: isMobile =', isMobile, ', isIOS =', isIOS, ', isAndroid =', isAndroid);
      return config;
    }
    // 🔧 IMPROVED: iPhone Chrome audio quality matches iOS Safari
    if (isChromeOnIOS) {
      return {
        ...baseConfig,
        audioBitsPerSecond: 96000,  // 96kbps - matches iOS Safari quality
        sampleRate: 44100,          // 44kHz - matches iOS Safari quality  
        numberOfAudioChannels: 1,
      };
    }
    
    return {
      ...baseConfig,
      audioBitsPerSecond: isMobile ? 96000 : 128000, // Lower for mobile networks
      sampleRate: isIOS ? 44100 : 48000,
    };
  };

  // 🎯 RECORDING FORMAT OPTIMIZATION: Debug format capabilities
  const detectFormatCapabilities = () => {
    const capabilities = { video: [], audio: [] };
    const videoFormats = [
      'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    const audioFormats = [
      'audio/wav',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/webm;codecs=opus',
      'audio/webm',
    ];

  videoFormats.forEach(f => { if (MediaRecorder.isTypeSupported(f)) capabilities.video.push(f); });
  audioFormats.forEach(f => { if (MediaRecorder.isTypeSupported(f)) capabilities.audio.push(f); });
  console.log('📱 Device Recording Capabilities:', capabilities);
  console.log('📱 Platform Info:', { isIOS, isAndroid, isChromeOnIOS, isSafariOnIOS, userAgent: navigator.userAgent });
  return capabilities;
};


  // Monitor state changes for debugging
  useEffect(() => {
    console.log('🔍 DEBUG: sessionComplete state changed to:', sessionComplete);
  }, [sessionComplete]);

  useEffect(() => {
    console.log('🔍 DEBUG: results state changed to:', results);
  }, [results]);

  // 🔧 FIX: Add effect to force re-render when results change
  useEffect(() => {
    if (results) {
      console.log('🔍 DEBUG: Results state updated, triggering re-render');
      console.log('🔍 DEBUG: New results data:', {
        heartRate: results.heartRate,
        bloodPressure: results.bloodPressure,
        heartRateVariability: results.heartRateVariability,
        rhythmRegularity: results.rhythmRegularity
      });
    }
  }, [results]);

  // 🔧 BP SPECIFIC FIX: Monitor BP changes to ensure re-render
  useEffect(() => {
    if (results && results.bloodPressure) {
      console.log('🔍 DEBUG: Blood Pressure state changed:', {
        systolic: results.bloodPressure.systolic,
        diastolic: results.bloodPressure.diastolic,
        map: results.bloodPressure.map
      });
      
      // Force a re-render by updating a dependency
      setForceUpdate(prev => prev + 1);
    }
  }, [results?.bloodPressure?.systolic, results?.bloodPressure?.diastolic, results?.bloodPressure?.map]);

  const steps = [
    {
      id: 1,
      title: "Video Recording",
      icon: Camera,
      description: `Record your face for ${videoDuration} seconds`,
      tips: ["Make sure your face is well-lit and clearly visible"],
      duration: videoDuration, // Use the state variable
      type: "video",
      showCamera: true
    },
    {
      id: 2,
      title: "Audio Capture",
      icon: Heart,
      description: `Record pulse audio for ${pulseDuration} seconds`,
      tips: [
        "Position the microphone slightly left of center on your chest",
        "Stay as still and quiet as possible",
        "Avoid background noise"
      ],
      duration: pulseDuration, // Use the state variable
      type: "pulse_audio",
      showCamera: false
    },
    {
      id: 3,
      title: "Voice Capture",
      icon: Mic,
      description: "Say 'Ahh' in a steady voice (2-second segments)",
      tips: [
        "Hold your phone 6–8 inches from your mouth",
        "Maintain a consistent tone and volume",
        "Follow the segment timing (2s record, 2s pause)"
      ],
      duration: 12, // 6 segments of 2 seconds each
      type: "vowel_audio",
      showCamera: false
    }
  ];

  const currentStepData = {
    ...steps[currentStep],
    description: currentStep === 0 ? `Record your face for ${videoDuration} seconds` : 
               currentStep === 1 ? `Record pulse audio for ${pulseDuration} seconds` : 
               steps[currentStep].description
  };

  // Check permissions on component mount
  useEffect(() => {
    checkPermissions();
    // 🎯 RECORDING FORMAT OPTIMIZATION: Log device capabilities on mount
    detectFormatCapabilities();
    
    // 🎯 Enhanced device logging for debugging (once on mount)
    console.log('🔍 Device Detection Results:', {
      isIOS,
      isAndroid,
      isChromeOnIOS,
      isSafariOnIOS,
      userAgent: navigator.userAgent
    });
  }, []);

  const checkPermissions = async () => {
    try {
      // Check camera permission
      const cameraPermission = await navigator.permissions.query({ name: 'camera' });
      setPermissions(prev => ({ ...prev, camera: cameraPermission.state }));
      
      // Check microphone permission
      const micPermission = await navigator.permissions.query({ name: 'microphone' });
      setPermissions(prev => ({ ...prev, microphone: micPermission.state }));
      
      // Listen for permission changes
      cameraPermission.onchange = () => {
        setPermissions(prev => ({ ...prev, camera: cameraPermission.state }));
      };
      
      micPermission.onchange = () => {
        setPermissions(prev => ({ ...prev, microphone: micPermission.state }));
      };
    } catch (error) {
      console.error('Permission check error:', error);
    }
  };

  const requestPermissions = async () => {
    setPermissionRequested(true);
    
    try {
      console.log('🔍 DEBUG: Requesting camera permission...');
      
      // Simplified permission check - just request basic video access
      const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
      cameraStream.getTracks().forEach(track => track.stop());
      setPermissions(prev => ({ ...prev, camera: 'granted' }));
      console.log('🔍 DEBUG: Camera permission granted');
      
      console.log('🔍 DEBUG: Requesting microphone permission...');
      // Request microphone permission
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStream.getTracks().forEach(track => track.stop());
      setPermissions(prev => ({ ...prev, microphone: 'granted' }));
      console.log('🔍 DEBUG: Microphone permission granted');
      
    } catch (error) {
      console.error('🔍 DEBUG: Permission request error:', error);
      
      let errorMessage = 'Permission request failed';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera/microphone access denied. Please allow access in your browser settings and refresh the page.';
        setPermissions(prev => ({ 
          camera: 'denied', 
          microphone: 'denied' 
        }));
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'Camera or microphone not found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera or microphone is already in use by another application.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (error.name === 'TypeError') {
        errorMessage = 'Camera/microphone access is not supported on this device.';
      }
      
      toast.error(errorMessage);
    } finally {
      setPermissionRequested(false);
    }
  };

  // Initialize camera only for Step 1 (Video Recording)
  useEffect(() => {
    if (currentStepData.showCamera && permissions.camera === 'granted') {
      initializeCamera();
    } else {
      cleanupCamera();
    }
    return () => cleanupCamera();
  }, [currentStepData.showCamera, permissions.camera]);

  // Timer effect - countdown for Step 1 and Step 2
  useEffect(() => {
    if (recording) {
      // For Step 1 (Video Recording) and Step 2 (Pulse Audio), use countdown
      if (currentStepData.type === "video") {
        const duration = videoDuration; // Use the state variable
        setRecordingTime(duration);
        
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => {
            console.log('⏰⏰⏰ TIMER TICK:', prev, 'Step:', currentStep);
            if (prev <= 1) {
              console.log('⏰⏰⏰ TIMER REACHED 1, CALLING STOP RECORDING');
              stopRecording();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (currentStepData.type === "pulse_audio") {
        const duration = pulseDuration; // Use the state variable for pulse audio
        setRecordingTime(duration);
        
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => {
            console.log('⏰⏰⏰ TIMER TICK:', prev, 'Step:', currentStep);
            if (prev <= 1) {
              console.log('⏰⏰⏰ TIMER REACHED 1, CALLING STOP RECORDING');
              stopRecording();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (currentStepData.type === "vowel_audio") {
        // For vowel audio, use 15 second countdown
        const duration = 15; // Fixed 15 second duration for vowel audio
        setRecordingTime(duration);
        
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => {
            console.log('⏰⏰⏰ TIMER TICK:', prev, 'Step:', currentStep);
            if (prev <= 1) {
              console.log('⏰⏰⏰ TIMER REACHED 1, CALLING STOP RECORDING');
              stopRecording();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [recording, currentStepData.duration, currentStepData.type, videoDuration, pulseDuration]);

  // Audio analysis effect for Steps 2 & 3
  useEffect(() => {
    if (recording && !currentStepData.showCamera && permissions.microphone === 'granted') {
      if (currentStepData.type === 'pulse_audio') {
        startPulseAnalysis();
      } else if (currentStepData.type === 'vowel_audio') {
        startVowelAnalysis();
      }
    } else {
      stopAudioAnalysis();
    }
  }, [recording, currentStepData.showCamera, currentStepData.type, permissions.microphone]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      cleanupRecorders();
      cleanupStreams();
      stopAudioAnalysis();
    };
  }, []);

  // Simple smoothing function
  const smoothEkgData = (data) => {
    if (data.length < 3) return data;
    
    const smoothed = [];
    for (let i = 0; i < data.length; i++) {
      if (i === 0 || i === data.length - 1) {
        smoothed.push(data[i]);
      } else {
        // Simple 3-point average
        const prev = data[i - 1].amplitude;
        const current = data[i].amplitude;
        const next = data[i + 1].amplitude;
        
        const smoothedAmplitude = (prev + current + next) / 3;
        
        smoothed.push({
          ...data[i],
          amplitude: smoothedAmplitude
        });
      }
    }
    
    return smoothed;
  };

  const renderEcgWaveform = () => {
    const canvas = videoRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas with midnight navy background
    ctx.fillStyle = '#0a0a2a';
    ctx.fillRect(0, 0, width, height);

    // Draw subtle grid lines
    ctx.strokeStyle = '#1a1a3a';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < width; x += 25) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += 25) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Create realistic ECG intervals with P-QRS-T cycle
    const time = Date.now() * 0.001; // Current time for animation
    const audioLevel = ekgData.length > 0 ? ekgData[ekgData.length - 1]?.amplitude || 0 : 0;
    const hasAudioInput = audioLevel > 15;
    
    // Only show ECG if recording is active and audio is detected
    if (!recording) {
      // Show black screen before recording starts
      return;
    }
    
    // Check for audio gaps (2-4 seconds of no audio)
    const currentTime = Date.now();
    const lastAudioTime = ekgData.length > 0 ? ekgData[ekgData.length - 1]?.timestamp || 0 : 0;
    const audioGap = (currentTime - lastAudioTime) / 1000; // Gap in seconds
    
    // Show black screen if no audio for 2+ seconds
    if (audioGap > 2) {
      return;
    }
    
    // Only draw ECG if we have recent audio input
    if (!hasAudioInput) {
      return;
    }
    
    ctx.strokeStyle = '#00ff00'; // Bright green ECG line
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    
    // Heart rate parameters (60-100 BPM range)
    const heartRate = 72; // Beats per minute
    const beatInterval = 60 / heartRate; // Seconds per beat
    const waveSpeed = 2; // Speed of wave movement

    // Draw ECG wave with proper intervals
    for (let x = 0; x < width; x += 2) {
      const normalizedX = x / width;
      const waveTime = time + normalizedX * waveSpeed;
      
      // Calculate beat phase (0 to 1 for one complete heartbeat)
      const beatPhase = (waveTime % beatInterval) / beatInterval;
      
      // Create realistic P-QRS-T intervals
      let ecgAmplitude = 0;
      
      if (beatPhase < 0.1) {
        // P wave interval (0-10% of beat)
        const pPhase = beatPhase / 0.1;
        ecgAmplitude = Math.sin(pPhase * Math.PI) * height * 0.08;
      } else if (beatPhase < 0.15) {
        // PR segment (10-15% of beat) - flat baseline
        ecgAmplitude = 0;
      } else if (beatPhase < 0.25) {
        // QRS complex (15-25% of beat)
        const qrsPhase = (beatPhase - 0.15) / 0.1;
        // Sharp spike for QRS
        ecgAmplitude = Math.sin(qrsPhase * Math.PI * 2) * Math.exp(-Math.abs(qrsPhase - 0.5) * 4) * height * 0.4;
      } else if (beatPhase < 0.35) {
        // ST segment (25-35% of beat) - flat baseline
        ecgAmplitude = 0;
      } else if (beatPhase < 0.6) {
        // T wave (35-60% of beat)
        const tPhase = (beatPhase - 0.35) / 0.25;
        ecgAmplitude = Math.sin(tPhase * Math.PI) * height * 0.15;
      } else {
        // TP segment (60-100% of beat) - flat baseline
        ecgAmplitude = 0;
      }
      
      // Add audio level influence
      const audioInfluence = (audioLevel / 255) * height * 0.1;
      ecgAmplitude += audioInfluence;
      
      const y = height - ecgAmplitude - (height * 0.4);
      
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    
    ctx.stroke();
  };

  const renderVowelCanvas = () => {
    const canvas = vowelCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas with midnight navy background (same as EKG)
    ctx.fillStyle = '#0a0a2a'; // Midnight navy background
    ctx.fillRect(0, 0, width, height);

    // Draw subtle grid lines (same as EKG)
    ctx.strokeStyle = '#1a1a3a';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < width; x += 25) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += 25) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Draw zigzag ECG-style waveform (same as EKG)
    if (vowelWaveform.length > 1) {
      ctx.strokeStyle = '#ffffff'; // Pure white waveform
      ctx.lineWidth = 1.5; // Thin line for precision
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();

      const stepX = width / vowelWaveform.length;
      
      // Create zigzag pattern with sharp peaks (same as EKG)
      vowelWaveform.forEach((data, index) => {
        const x = index * stepX;
        
        // Check if there's actual audio input (not at rest)
        const hasInput = data.amplitude > 10; // Threshold for detecting input
        
        if (hasInput) {
          // Create zigzag pattern with sharp peaks and valleys
          const baseAmplitude = (data.amplitude / 255) * height * 0.6;
          
          // Zigzag wave components
          const zigzag1 = Math.sin(index * 2.5) * height * 0.2; // Sharp zigzag
          const zigzag2 = Math.sin(index * 1.8) * height * 0.15; // Secondary zigzag
          const zigzag3 = Math.sin(index * 3.2) * height * 0.1; // Fine zigzag detail
          
          // Combine to create pronounced zigzag pattern
          const zigzagAmplitude = baseAmplitude + zigzag1 + zigzag2 + zigzag3;
          const y = height - zigzagAmplitude - (height * 0.2);
          
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        } else {
          // Flat baseline when no input
          const y = height - (height * 0.2);
          
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
      });
      ctx.stroke();
    } else {
      // Draw flat baseline when no data
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, height - (height * 0.2));
      ctx.lineTo(width, height - (height * 0.2));
      ctx.stroke();
    }
  };

  // ECG rendering effects with continuous animation
  useEffect(() => {
    if (currentStepData.type === 'pulse_audio' && videoRef.current) {
      let animationId;
      
      const animateEcg = () => {
        renderEcgWaveform();
        animationId = requestAnimationFrame(animateEcg);
      };
      
      animateEcg();
      
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [currentStepData.type, ekgData]);

  useEffect(() => {
    if (vowelWaveform.length > 0 && vowelCanvasRef.current) {
      renderVowelCanvas();
    }
  }, [vowelWaveform]);

  const initializeCamera = async () => {
    try {
      // Check if permissions are already granted
      if (permissions.camera !== 'granted') {
        console.log('🔍 DEBUG: Camera permission not granted, requesting...');
        
        console.log('🔍 DEBUG: Camera permission not granted, using requestPermissions flow...');
        await requestPermissions();
      }
      
      // Use optimized stream creation instead
      console.log('🔍 DEBUG: Using optimized getOrCreateStream for camera...');
      const stream = await getOrCreateStream(true);
      
      if (videoRef.current) {
        // Set video properties before setting srcObject
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        videoRef.current.autoplay = true;
        
        // Set the stream
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // 🎯 PHASE 3: Log stream resolution to verify our constraints worked
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
        }
        
        // Wait for video to be ready
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Video initialization timeout')), 10000);
          
          const handleCanPlay = () => {
            clearTimeout(timeout);
            videoRef.current.removeEventListener('canplay', handleCanPlay);
            resolve();
          };
          
          const handleError = (error) => {
            clearTimeout(timeout);
            videoRef.current.removeEventListener('error', handleError);
            reject(error);
          };
          
          videoRef.current.addEventListener('canplay', handleCanPlay);
          videoRef.current.addEventListener('error', handleError);
        });
      }
      
      setCameraReady(true);
      setCameraError(null);
      setShowCamera(true);
      console.log('🔍 DEBUG: Camera initialized successfully');
    } catch (error) {
      console.error('🔍 DEBUG: Camera initialization error:', error);
      
      let errorMessage = 'Camera access failed';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera access and refresh the page.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (error.name === 'TypeError') {
        errorMessage = 'Camera access is not supported on this device.';
      }
      
      setCameraError(errorMessage);
      setCameraReady(false);
      setShowCamera(false);
    }
  };

  const cleanupCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const startPulseAnalysis = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      microphoneRef.current = stream;

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 512;
      analyserRef.current.smoothingTimeConstant = 0.8;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const timeDataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const updatePulseAnalysis = () => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate average frequency with better sensitivity
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        setAudioLevel(average);
        
        // Update EKG data with more responsive feedback
        const newData = {
          amplitude: average,
          timestamp: Date.now(),
          peaks: average > 20 ? 1 : 0 // Lower threshold for better detection
        };
        
        setEkgData(prev => [...prev.slice(-50), newData]);
        // Trigger ECG waveform rendering
      };

      // Clear any existing interval
      if (pulseIntervalRef.current) {
        clearInterval(pulseIntervalRef.current);
      }
      
      // Store the new interval
      pulseIntervalRef.current = setInterval(updatePulseAnalysis, 200);
    } catch (error) {
      console.error('Pulse analysis error:', error);
    }
  };

  const startVowelAnalysis = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      microphoneRef.current = stream;

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 512; // Changed to match EKG analysis
      analyserRef.current.smoothingTimeConstant = 0.8; // Added smoothing like EKG

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateVowelAnalysis = () => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate average frequency with better sensitivity (same as EKG)
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        setAudioLevel(average);
        
        // Update vowel data with EKG-style data structure
        const newData = {
          amplitude: average,
          timestamp: Date.now(),
          peaks: average > 20 ? 1 : 0 // Lower threshold for better detection (same as EKG)
        };
        
        setVowelWaveform(prev => [...prev.slice(-50), newData]);
        renderVowelCanvas();
      };

      // Clear any existing interval
      if (vowelIntervalRef.current) {
        clearInterval(vowelIntervalRef.current);
      }
      
      // Store the new interval
      vowelIntervalRef.current = setInterval(updateVowelAnalysis, 200);
    } catch (error) {
      console.error('Vowel analysis error:', error);
    }
  };

  const analyzePulsePattern = (timeData) => {
    // Simplified pulse pattern analysis
    let peaks = 0;
    let lastPeak = 0;
    const threshold = 128;
    
    for (let i = 1; i < timeData.length - 1; i++) {
      if (timeData[i] > threshold && timeData[i] > timeData[i-1] && timeData[i] > timeData[i+1]) {
        if (i - lastPeak > 10) { // Minimum distance between peaks
          peaks++;
          lastPeak = i;
        }
      }
    }
    
    return {
      amplitude: Math.max(...timeData),
      peaks: peaks,
      timestamp: Date.now()
    };
  };

  const calculatePulseRate = (pulseData) => {
    // Calculate BPM from pulse data
    if (pulseData.peaks > 0) {
      const timeWindow = 15000; // 15 seconds
      const bpm = (pulseData.peaks * 60) / (timeWindow / 1000);
      return Math.round(bpm);
    }
    return 0;
  };

  const analyzeVowelWaveform = (frequencyData) => {
    // Analyze vowel characteristics
    const dominantFreq = frequencyData.indexOf(Math.max(...frequencyData));
    const averageAmplitude = frequencyData.reduce((a, b) => a + b) / frequencyData.length;
    
    return {
      dominantFrequency: dominantFreq,
      amplitude: averageAmplitude,
      timestamp: Date.now()
    };
  };

  const stopAudioAnalysis = () => {
    // Clear intervals
    if (pulseIntervalRef.current) {
      clearInterval(pulseIntervalRef.current);
      pulseIntervalRef.current = null;
    }
    if (vowelIntervalRef.current) {
      clearInterval(vowelIntervalRef.current);
      vowelIntervalRef.current = null;
    }
    
    if (microphoneRef.current) {
      microphoneRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (error) {
        console.warn('AudioContext already closed:', error);
      }
    }
    setFrequencyData([]);
    setAudioLevel(0);
    setEkgData([]);
    setVowelWaveform([]);
    setPulseRate(0);
  };

  // Storage functions
  const uploadVideoRecording = async (videoBlob) => {
    // 🔧 CRITICAL FIX: Use existing session ID, don't generate new one
    if (!sessionId) {
      console.error('🔍 DEBUG: No session ID available for video upload');
      throw new Error('No session ID available. Please start recording first.');
    }
    
    try {
      console.log('🔍 DEBUG: Using existing session ID for video upload:', sessionId);
      const response = await vitalSignsAPI.uploadVideoRecording(sessionId, videoBlob, {
        duration: videoDuration,
        step: 1,
        recordingType: 'video'
      });
      
      const recordingInfo = {
        type: 'video',
        filePath: response.data.file_path,
        duration: videoDuration,
        timestamp: new Date().toISOString()
      };
      
      setStoredRecordings(prev => [...prev, recordingInfo]);
      setRecordingMetadata(prev => ({ ...prev, video: recordingInfo }));
      toast.success('Video recording saved!');
    } catch (error) {
      console.error('Video upload failed:', error);
      toast.error('Failed to save video recording');
      throw error;
    }
  };

  const uploadPulseAudio = async (audioBlob) => {
    console.log('🔍 DEBUG: uploadPulseAudio called with blob size:', audioBlob.size);
    
    // 🔧 CRITICAL FIX: Use existing session ID, don't generate new one
    if (!sessionId) {
      console.error('🔍 DEBUG: No session ID available for pulse audio upload');
      throw new Error('No session ID available. Please start video recording first.');
    }
    
    try {
      console.log('🔍 DEBUG: Using existing session ID for pulse audio:', sessionId);
      const response = await vitalSignsAPI.uploadPulseAudio(sessionId, audioBlob, {
        duration: pulseDuration,
        step: 2,
        recordingType: 'pulse_audio',
        audioAnalysis: {
          averageLevel: audioLevel,
          frequencyData: frequencyData.slice(-10)
        }
      });
      
      console.log('🔍 DEBUG: Upload response:', response.data);
      
      const recordingInfo = {
        type: 'pulse_audio',
        filePath: response.data.file_path,
        duration: pulseDuration,
        timestamp: new Date().toISOString()
      };
      
      setStoredRecordings(prev => [...prev, recordingInfo]);
      setRecordingMetadata(prev => ({ ...prev, pulse: recordingInfo }));
      toast.success('Pulse audio saved!');
      console.log('🔍 DEBUG: Pulse audio upload completed successfully');
    } catch (error) {
      console.error('Pulse audio upload failed:', error);
      console.error('🔍 DEBUG: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Failed to save pulse audio');
      throw error;
    }
  };

  const uploadVowelAudio = async (audioBlob) => {
    // 🔧 CRITICAL FIX: Use existing session ID, don't generate new one
    if (!sessionId) {
      console.error('🔍 DEBUG: No session ID available for vowel audio upload');
      throw new Error('No session ID available. Please start video recording first.');
    }
    
    try {
      console.log('🔍 DEBUG: Using existing session ID for vowel audio:', sessionId);
      const response = await vitalSignsAPI.uploadVowelAudio(sessionId, audioBlob, {
        duration: 12,
        step: 3,
        recordingType: 'vowel_audio',
        audioAnalysis: {
          averageLevel: audioLevel,
          frequencyData: frequencyData.slice(-10)
        }
      });
      
      const recordingInfo = {
        type: 'vowel_audio',
        filePath: response.data.file_path,
        duration: 12,
        timestamp: new Date().toISOString()
      };
      
      setStoredRecordings(prev => [...prev, recordingInfo]);
      setRecordingMetadata(prev => ({ ...prev, vowel: recordingInfo }));
      toast.success('Vowel audio saved!');
    } catch (error) {
      console.error('Vowel audio upload failed:', error);
      toast.error('Failed to save vowel audio');
      throw error;
    }
  };

  // 🔧 DEBUG: Direct API test function
  const testDirectAPI = async () => {
    try {
      console.log('🧪 DEBUG: Testing direct API call...');
      
      // Test health endpoint first
      console.log('🏥 Testing health endpoint...');
              const healthResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/vital-signs-analysis-simple/health`);
      console.log('🏥 Health response status:', healthResponse.status);
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        console.log('🏥 Health data:', healthData);
      }
      
      // Test test-algorithms endpoint
      console.log('🧪 Testing test-algorithms endpoint...');
              const testResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/vital-signs-analysis-simple/test-algorithms`, {
        method: 'POST'
      });
      console.log('🧪 Test response status:', testResponse.status);
      if (testResponse.ok) {
        const testData = await testResponse.json();
        console.log('🧪 Test data:', testData);
        console.log('🧪 Test data keys:', Object.keys(testData));
        if (testData.test_results && testData.test_results.combined_results) {
          console.log('🧪 Found combined_results:', testData.test_results.combined_results);
        }
      }
      
      toast.success('Direct API test completed - check console');
    } catch (error) {
      console.error('🧪 Direct API test failed:', error);
      toast.error('Direct API test failed');
    }
  };

  const completeVitalSignsSession = async () => {
    console.log('🚀🚀🚀 COMPLETE VITAL SIGNS SESSION CALLED 🚀🚀🚀');
    console.log('🚀🚀🚀 SESSION ID:', sessionId);
    console.log('🚀🚀🚀 PROPS:', props);
    
    // 🔧 CRITICAL FIX: Use existing session ID, don't generate new one
    if (!sessionId) {
      console.error('🔍 DEBUG: No session ID available for session completion');
      toast.error('No recording session started. Please start recording first.');
      return;
    }
   
    // Use the existing session ID consistently
    const finalSessionId = sessionId;
    const rridFromProps = props?.recordingRequestId || null;
    const linkToken = props?.token || null;
  const patientId = props?.patientId || null;
  console.log('🔍 DEBUG: Using existing session ID:', finalSessionId);
  console.log('🔍 DEBUG: Props received:', props);
  console.log('🔍 DEBUG: PatientId extracted:', patientId);
  console.log('🔍 DEBUG: PatientId type:', typeof patientId);
  console.log('🔍 DEBUG: PatientId truthy:', !!patientId);
   
    try {
      console.log('🔍 DEBUG: Starting session completion for session:', finalSessionId, ' rrid:', rridFromProps, ' token present:', !!linkToken);
      console.log('🔍 DEBUG: Stored recordings:', storedRecordings);
     
      // 🔧 FIX: Clear old results before starting new analysis
      setResults(null);
      console.log('🔍 DEBUG: Cleared old results state');
      
      // Set initial processing stage
      setProcessingStage('Uploading files...');
      setPollingProgress(0);
     
      // 🎯 MOBILE TIMEOUT RESILIENCE: Complete vital signs session with timeout handling
      let storageResp = null;
      let sessionCompletionTimedOut = false;
      
      try {
        console.log('🔍 DEBUG: Starting session completion with mobile timeout resilience...');
        console.log(`📱 MOBILE DEVICE: ${isMobileDevice}, TIMEOUT: ${isMobileDevice ? '5 minutes' : '10 minutes'}`);
        
        // Create timeout controller - longer for mobile due to slower processing
        const timeoutMs = isMobileDevice ? 300000 : 600000; // 5min mobile, 10min desktop
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.warn(`⏰ SESSION COMPLETION TIMEOUT: Exceeded ${timeoutMs/1000}s - falling back to polling`);
          controller.abort();
        }, timeoutMs);

        try {
          storageResp = await vitalSignsAPI.completeVitalSignsSession(finalSessionId, storedRecordings, rridFromProps, controller.signal);
          clearTimeout(timeoutId); // Success - clear timeout
          
          console.log('✅ Session completion succeeded within timeout');
          console.log('🔍 DEBUG: Storage completion response:', storageResp);
          console.log('🔍 DEBUG: Storage response type:', typeof storageResp);
          console.log('🔍 DEBUG: Storage response keys:', storageResp ? Object.keys(storageResp) : 'null');
          console.log('🔍 DEBUG: Storage response stringified:', JSON.stringify(storageResp, null, 2));
          
        } catch (requestError) {
          clearTimeout(timeoutId); // Clear timeout on any error
          
          // Check if this is a timeout/abort error
          if (requestError.name === 'AbortError' || 
              requestError.message?.includes('timeout') || 
              requestError.message?.includes('aborted')) {
            
            console.warn('⏰ TIMEOUT DETECTED: Session completion request timed out');
            console.warn(`📱 MOBILE IMPACT: ${isMobileDevice ? 'Expected on mobile networks' : 'Unexpected on desktop'}`);
            console.warn('🔄 FALLBACK STRATEGY: Starting polling for results (backend may still be processing)');
            
            sessionCompletionTimedOut = true;
            
            // Show user that we're falling back to polling
            toast.info(`Processing is taking longer than expected. Monitoring progress...`, {
              toastId: 'session-timeout-fallback',
            });
            
          } else {
            // Real error, not timeout - propagate it
            throw requestError;
          }
        }
        
      } catch (error) {
        // Real error in setup - propagate
        throw error;
      }
      
      // 🔍 VERIFY STORAGE COMPLETION (if we got a response)
      if (storageResp) {
        if (!storageResp || storageResp.error) {
          console.error('🔍 DEBUG: Storage completion failed:', storageResp);
          throw new Error('Storage completion failed - cannot proceed with analysis');
        }
        console.log('🔍 DEBUG: Storage completion verified successfully');
      } else if (sessionCompletionTimedOut) {
        console.log('🔄 DEBUG: No storage response due to timeout - will rely on polling for results');
      } else {
        throw new Error('No storage response received and no timeout detected');
      }
 
      // 🔧 REMOVED: Vitals summary saving moved to after analysis results are available
 
      // 🔧 CRITICAL FIX: Complete the recording link BEFORE setting sessionComplete (silently)
      try {
        if (linkToken) {
          console.log('🛰️ Completing recording link to expire session before showing results...', linkToken);
          const { recordingAPI } = await import('../services/api/api');
          await recordingAPI.completeSession(linkToken);
          console.log('✅ Recording link completed successfully - session will expire on refresh');
        } else {
          console.warn('⚠️ No link token available for recording completion');
        }
      } catch (linkError) {
        console.warn('⚠️ Recording link completion failed (non-critical):', linkError.message);
        // Continue silently to show results - session expiration handled gracefully
      }

      console.log('🔍 DEBUG: Setting sessionComplete to true');
      setSessionComplete(true);
      console.log('🔍 DEBUG: sessionComplete state set to true');

      // 🎯 CHECK FOR IMMEDIATE RESULTS (if session completion succeeded)
      if (storageResp && storageResp.algorithm_results) {
        console.log('✅ IMMEDIATE RESULTS: Found algorithm results in storage response');
        console.log('🔍 DEBUG: Immediate results data:', storageResp.algorithm_results);
        
        // Convert and set results immediately - no polling needed!
        try {
          const convertedResults = vitalSignsAnalysisAPI.convertAPIResponseToResults({
            vital_signs: storageResp.algorithm_results
          });
          
          setResults(convertedResults);
          setProcessingStage('');
          setPollingProgress(100);
          toast.success('Vital signs analysis completed successfully!');
          
          console.log('✅ IMMEDIATE RESULTS SUCCESS: Results set directly from storage response');
          return; // Skip polling entirely
          
        } catch (conversionError) {
          console.warn('⚠️ IMMEDIATE RESULTS CONVERSION FAILED:', conversionError.message);
          console.warn('🔄 FALLBACK: Will continue with polling to get results from database');
          // Continue to polling as fallback
        }
      } else if (sessionCompletionTimedOut) {
        console.log('⏰ TIMEOUT RECOVERY: Session completion timed out - starting polling immediately');
        console.log('🔄 BACKEND STATUS: Backend is likely still processing, polling will wait for completion');
        setProcessingStage('Backend processing - monitoring progress...');
      } else {
        console.log('🔄 NO IMMEDIATE RESULTS: Algorithm results not ready, starting polling');
        setProcessingStage('Processing recordings...');
      }
     
      // 🔄 INTELLIGENT POLLING: Wait for backend processing to complete
      console.log('🔍 DEBUG: Starting intelligent polling for processing completion...');
      if (!sessionCompletionTimedOut) {
        setProcessingStage('Processing recordings...');
      }
     
      // Now get real vital signs analysis from our API
      console.log('🔍 DEBUG: Attempting to analyze recordings...');
     
      // First check if the backend is accessible
      try {
        console.log('🔍 DEBUG: Checking backend health...');
        const healthCheck = await vitalSignsAnalysisAPI.healthCheck();
        console.log('🔍 DEBUG: Backend health check:', healthCheck);
      } catch (healthError) {
        console.error('🔍 DEBUG: Backend health check failed:', healthError);
        console.log('🔍 DEBUG: Backend may be unavailable, proceeding with fallbacks...');
       
        // Check if it's a network error
        if (healthError.message.includes('Failed to fetch') || healthError.message.includes('NetworkError')) {
          console.error('🔍 DEBUG: Network connectivity issue detected');
          toast.error('Backend server is not accessible. Please check if the server is running.');
         
          // Try a simple fetch test
          try {
            console.log('🔍 DEBUG: Testing simple fetch to backend...');
            const testResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/vital-signs-analysis-simple/health`);
            console.log('🔍 DEBUG: Simple fetch test response status:', testResponse.status);
            if (testResponse.ok) {
              const testData = await testResponse.json();
              console.log('🔍 DEBUG: Simple fetch test data:', testData);
            }
          } catch (fetchError) {
            console.error('🔍 DEBUG: Simple fetch test also failed:', fetchError);
          }
        }
      }
     
      try {
        console.log('🔍 DEBUG: Calling processStoredRecordings...');
        console.log('🔍 DEBUG: Session ID being passed to API:', finalSessionId);
        console.log('🔍 DEBUG: Session ID type:', typeof finalSessionId);
        console.log('🔍 DEBUG: Session ID length:', finalSessionId ? finalSessionId.length : 'null');
        
        // 🔄 RETRY MECHANISM COMMENTED OUT - Direct single call to get results from database
        // let analysisResults = null;
        // let retryCount = 0;
        // const maxRetries = 3;
        
        // 🔄 INTELLIGENT POLLING CONFIGURATION
        const POLLING_CONFIG = {
          // 🎯 TIMEOUT RECOVERY: Adjust timing based on whether session completion timed out
          initialWait: sessionCompletionTimedOut 
            ? 1000  // Very short wait - backend is already processing
            : (isMobileDevice ? 5000 : 3000),               // Normal wait times
          maxAttempts: sessionCompletionTimedOut 
            ? (isMobileDevice ? 40 : 35)  // More attempts for timeout recovery
            : (isMobileDevice ? 30 : 25),                   // Normal attempts
          baseInterval: isMobileDevice ? 3000 : 2000,       // Mobile: longer intervals
          maxInterval: isMobileDevice ? 12000 : 8000,       // Mobile: max 12s intervals
          backoffMultiplier: 1.2,
          timeoutMs: sessionCompletionTimedOut
            ? (isMobileDevice ? 360000 : 300000)  // Longer timeout for recovery (6/5 min)
            : (isMobileDevice ? 240000 : 180000)            // Normal timeout (4/3 min)
        };

        console.log(`🔍 DEVICE TYPE: ${isMobileDevice ? 'Mobile' : 'Desktop'}, Slow connection: ${isSlowConnection}`);
        console.log(`⏰ TIMEOUT RECOVERY MODE: ${sessionCompletionTimedOut ? 'YES - Backend already processing' : 'NO - Normal flow'}`);
        console.log(`🔄 POLLING CONFIG: initialWait=${POLLING_CONFIG.initialWait}ms, maxAttempts=${POLLING_CONFIG.maxAttempts}, timeout=${POLLING_CONFIG.timeoutMs/1000}s`);

        const startTime = Date.now();
        let analysisResults = null;
        let attempt = 0;
        let currentInterval = POLLING_CONFIG.baseInterval;

        // Initial wait - minimal if recovering from timeout
        if (sessionCompletionTimedOut) {
          console.log(`⏰ TIMEOUT RECOVERY: Minimal wait ${POLLING_CONFIG.initialWait}ms (backend likely processing)...`);
        } else {
          console.log(`🔍 DEBUG: Initial wait ${POLLING_CONFIG.initialWait}ms for processing to start...`);
        }
        await new Promise(resolve => setTimeout(resolve, POLLING_CONFIG.initialWait));

        // Start polling loop
        while (attempt < POLLING_CONFIG.maxAttempts) {
          attempt++;
          const elapsedTime = Date.now() - startTime;
          
          // Check total timeout
          if (elapsedTime > POLLING_CONFIG.timeoutMs) {
            console.error(`🚨 POLLING TIMEOUT: Exceeded ${POLLING_CONFIG.timeoutMs/1000}s total timeout`);
            setProcessingStage('');
            throw new Error(`Processing timeout after ${Math.floor(elapsedTime/1000)} seconds`);
          }
          
          console.log(`🔄 POLLING ATTEMPT ${attempt}/${POLLING_CONFIG.maxAttempts} (after ${Math.floor(elapsedTime/1000)}s)`);
          
          // Update progress
          const progressPercent = Math.min(95, Math.floor((elapsedTime / POLLING_CONFIG.timeoutMs) * 100));
          setPollingProgress(progressPercent);
          
          try {
            // Try to get results
          analysisResults = await vitalSignsAnalysisAPI.analyzeExistingSession(finalSessionId);
            
            if (analysisResults && analysisResults.vital_signs) {
              console.log(`✅ SUCCESS: Results found after ${attempt} attempts (${Math.floor(elapsedTime/1000)}s)`);
              setProcessingStage('Generating results...');
              setPollingProgress(100);
              break; // Success! Exit polling loop
            } else {
              console.log(`⚠️  ATTEMPT ${attempt}: Results structure incomplete, continuing...`);
              analysisResults = null; // Clear incomplete results
            }
            
          } catch (pollingError) {
            const isNotFoundError = pollingError.message?.includes('404') || 
                                   pollingError.message?.includes('not found') ||
                                   pollingError.message?.includes('No analysis results found');
            
            if (isNotFoundError) {
              // 404 = Still processing, this is expected
              console.log(`⏳ ATTEMPT ${attempt}: Still processing (404 expected), waiting ${currentInterval}ms...`);
            } else {
              // Real error, not just "not ready"
              console.error(`❌ POLLING ERROR on attempt ${attempt}:`, pollingError.message);
              setProcessingStage('');
              setPollingProgress(0);
              throw pollingError; // Propagate real errors
            }
          }
          
          // If we're here, we need to wait and try again
          if (attempt < POLLING_CONFIG.maxAttempts) {
            // Show user feedback every 3rd attempt with appropriate message
            if (attempt % 3 === 0) {
              const feedbackMessage = sessionCompletionTimedOut 
                ? `Backend processing continues... ${Math.floor(elapsedTime/1000)}s monitoring`
                : `Processing your recordings... ${Math.floor(elapsedTime/1000)}s elapsed`;
              
              toast.info(feedbackMessage, {
                toastId: 'processing-progress', // Prevent duplicate toasts
              });
            }
            
            // Wait with exponential backoff
            await new Promise(resolve => setTimeout(resolve, currentInterval));
            
            // Increase interval for next attempt (exponential backoff)
            currentInterval = Math.min(
              POLLING_CONFIG.maxInterval, 
              currentInterval * POLLING_CONFIG.backoffMultiplier
            );
          }
        }

        // Check if we exhausted all attempts
        if (!analysisResults || !analysisResults.vital_signs) {
          const finalElapsedTime = Math.floor((Date.now() - startTime) / 1000);
          console.error(`🚨 POLLING EXHAUSTED: No results after ${attempt} attempts (${finalElapsedTime}s)`);
          setProcessingStage('');
          setPollingProgress(0);
          throw new Error(`Results not available after ${finalElapsedTime} seconds. Processing may still be running.`);
        }

        console.log(`✅ POLLING SUCCESS: Results retrieved after ${attempt} attempts`);
       
        console.log('🔍 DEBUG: Final analysis API response:', analysisResults);
        console.log('🔍 DEBUG: Analysis response type:', typeof analysisResults);
        console.log('🔍 DEBUG: Analysis response keys:', analysisResults ? Object.keys(analysisResults) : 'null');
        console.log('🔍 DEBUG: Analysis response is null/undefined:', analysisResults == null);
        console.log('🔍 DEBUG: Analysis response is empty object:', analysisResults && Object.keys(analysisResults).length === 0);
       
        if (analysisResults && analysisResults.vital_signs) {
          // ✅ Check for vital_signs FIRST (this is what backend actually returns)
          console.log('🔍 DEBUG: Found vital_signs in response:', analysisResults.vital_signs);
          const convertedResults = vitalSignsAnalysisAPI.convertAPIResponseToResults(analysisResults);
          console.log('🔍 DEBUG: Converted results from vital_signs:', convertedResults);
         
          // 🔧 CRITICAL FIX: Force a fresh state update with new data
          setResults(null); // Clear first
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
          setResults(convertedResults); // Set new data
         
          // 🔧 BP SPECIFIC FIX: Force BP state update to ensure re-render
          console.log('🔍 DEBUG: BP values being set:', {
            systolic: convertedResults.bloodPressure?.systolic,
            diastolic: convertedResults.bloodPressure?.diastolic,
            map: convertedResults.bloodPressure?.map
          });
         
          console.log('🔍 DEBUG: Results state updated with fresh data');
          
          // ✅ DUPLICATE SAVE REMOVED: Algorithm results are now saved only via backend path
          // The save_algorithm_results_to_database() function in vital_signs_storage_api.py 
          // handles saving to VitalEncounters table, eliminating duplicate records
          console.log('🔍 DEBUG: Vitals will be available via database from algorithm save path');
          
          toast.success('Vital signs analysis completed successfully!');
        } else if (analysisResults && analysisResults.data) {
          // ✅ Check for data field as fallback
          console.log('🔍 DEBUG: Found data field in response:', analysisResults.data);
          const convertedResults = vitalSignsAnalysisAPI.convertAPIResponseToResults(analysisResults);
          console.log('🔍 DEBUG: Converted results from data field:', convertedResults);
         
          // 🔧 CRITICAL FIX: Force a fresh state update with new data
          setResults(null); // Clear first
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
          setResults(convertedResults); // Set new data
         
          // 🔧 BP SPECIFIC FIX: Force BP state update to ensure re-render
          console.log('🔍 DEBUG: BP values being set:', {
            systolic: convertedResults.bloodPressure?.systolic,
            diastolic: convertedResults.bloodPressure?.diastolic,
            map: convertedResults.bloodPressure?.map
          });
         
          console.log('🔍 DEBUG: Results state updated with fresh data');
          
          // ✅ DUPLICATE SAVE REMOVED: Algorithm results are now saved only via backend path
          // The save_algorithm_results_to_database() function in vital_signs_storage_api.py 
          // handles saving to VitalEncounters table, eliminating duplicate records
          console.log('🔍 DEBUG: Vitals will be available via database from algorithm save path');
          
          toast.success('Vital signs analysis completed successfully!');
        } else if (analysisResults && analysisResults.test_results) {
          // ✅ Check for test_results field as another fallback
          console.log('🔍 DEBUG: Found test_results field in response:', analysisResults.test_results);
          const convertedResults = vitalSignsAnalysisAPI.convertAPIResponseToResults(analysisResults);
          console.log('🔍 DEBUG: Converted results from test_results field:', convertedResults);
         
          // 🔧 CRITICAL FIX: Force a fresh state update with new data
          setResults(null); // Clear first
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
          setResults(convertedResults); // Set new data
         
          // 🔧 BP SPECIFIC FIX: Force BP state update to ensure re-render
          console.log('🔍 DEBUG: BP values being set:', {
            systolic: convertedResults.bloodPressure?.systolic,
            diastolic: convertedResults.bloodPressure?.diastolic,
            map: convertedResults.bloodPressure?.map
          });
         
          console.log('🔍 DEBUG: Results state updated with fresh data');
         
          // 🔧 CRITICAL FIX: Complete the recording link to expire the session (silently)
          try {
            if (linkToken) {
              console.log('🛰️ Completing recording link to expire session (test_results path)...', linkToken);
              const { recordingAPI } = await import('../services/api/api');
              await recordingAPI.completeSession(linkToken);
              console.log('✅ Recording link completed successfully (test_results path) - session will expire on refresh');
            } else {
              console.warn('⚠️ No link token available for recording completion (test_results path)');
            }
          } catch (linkError) {
            console.warn('⚠️ Recording link completion failed (test_results path, non-critical):', linkError.message);
            // Silently handle this error - don't show toast or fail the process
          }
          
          toast.success('Vital signs analysis completed successfully!');
        } else {
          // ❌ No usable data found
          console.error('🔍 DEBUG: No usable data found in analysis response:', analysisResults);
          console.error('🔍 DEBUG: Analysis response details:', {
            type: typeof analysisResults,
            isNull: analysisResults === null,
            isUndefined: analysisResults === undefined,
            isEmpty: analysisResults && Object.keys(analysisResults).length === 0,
            keys: analysisResults ? Object.keys(analysisResults) : 'N/A',
            stringified: JSON.stringify(analysisResults, null, 2)
          });
          throw new Error('No data in analysis response');
        }
      } catch (analysisError) {
        console.error('🔍 DEBUG: Analysis/Polling failed:', analysisError);
        
        // Clear processing stage and progress
        setProcessingStage('');
        setPollingProgress(0);
        
        // Determine error type and show appropriate message
        if (analysisError.message?.includes('timeout') || analysisError.message?.includes('Timeout')) {
          console.error('🚨 TIMEOUT ERROR: Processing took too long');
          toast.error('Processing is taking longer than expected. Please try again or contact support if this persists.');
        } else if (analysisError.message?.includes('network') || analysisError.message?.includes('NetworkError')) {
          console.error('🚨 NETWORK ERROR: Connection issues detected');
          toast.error('Network connection issue. Please check your internet connection and try again.');
        } else if (analysisError.message?.includes('not available') || analysisError.message?.includes('not ready')) {
          console.error('🚨 PROCESSING ERROR: Results still not ready');
          toast.info('Your recordings are still being processed. Please wait a moment and try again.');
        } else {
          console.error('🚨 UNKNOWN ERROR:', analysisError.message);
          toast.error('An unexpected error occurred. Please try again.');
        }
        
        console.error('🔍 DEBUG: Full error details:', {
          message: analysisError.message,
          stack: analysisError.stack,
          name: analysisError.name
        });
        
        // Clear results and show error if analysis failed
        setResults(null);
        toast.error('Analysis failed - no results available. Please try recording again.');
        throw analysisError; // Re-throw to exit the function
      }
     
      toast.success('Vital signs session completed!');
    } catch (error) {
      console.error('🔍 DEBUG: Session completion failed:', error);
      console.error('🔍 DEBUG: Session error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast.error('Failed to complete session');
     
      // 🔧 MOBILE FIX: Don't call deprecated test-algorithms endpoint
      // Simply show processing message - algorithm may still be running in background
      console.log('🔍 DEBUG: Session completion had issues, but algorithm may still be processing...');
      toast.info('Processing...');
      
      // Results may become available after processing completes
      // Users can check their dashboard or try again if needed
    }
  };

  const cleanupRecorders = () => {
    // Clean up video recorder
    if (videoRecorderRef.current && typeof videoRecorderRef.current === 'object') {
      try {
        if (videoRecorderRef.current.state && videoRecorderRef.current.state === 'recording') {
          videoRecorderRef.current.stopRecording();
        }
        videoRecorderRef.current = null;
      } catch (error) {
        console.warn('Error cleaning up video recorder:', error);
        videoRecorderRef.current = null;
      }
    }
    
    // Clean up audio recorder
    if (audioRecorderRef.current && typeof audioRecorderRef.current === 'object') {
      try {
        if (audioRecorderRef.current.state && audioRecorderRef.current.state === 'recording') {
          audioRecorderRef.current.stopRecording();
        }
        audioRecorderRef.current = null;
      } catch (error) {
        console.warn('Error cleaning up audio recorder:', error);
        audioRecorderRef.current = null;
      }
    }
  };

  const cleanupStreams = () => {
    // Stop all tracks in the streams
    if (streamRef.current && typeof streamRef.current === 'object') {
      try {
        streamRef.current.getTracks().forEach(track => {
          if (track && typeof track.stop === 'function') {
            track.stop();
          }
        });
      } catch (error) {
        console.warn('Error cleaning up video stream:', error);
      }
      streamRef.current = null;
    }
    
    if (microphoneRef.current && typeof microphoneRef.current === 'object') {
      try {
        microphoneRef.current.getTracks().forEach(track => {
          if (track && typeof track.stop === 'function') {
            track.stop();
          }
        });
      } catch (error) {
        console.warn('Error cleaning up audio stream:', error);
      }
      microphoneRef.current = null;
    }
  };

  const getOrCreateStream = async (needsVideo = false) => {
    try {
      // 🎯 RECORDING FORMAT OPTIMIZATION: Device-optimized stream constraints
      const isMobile = isIOS || isAndroid;
      
      const constraints = {
        audio: {
          // 🔧 IMPROVED: iPhone Chrome audio quality matches iOS Safari
          sampleRate: isChromeOnIOS ? 44100 : (isIOS ? 44100 : 48000),  // 44kHz for iPhone Chrome (improved)
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      };
      
      if (needsVideo) {
        // 🎯 RECORDING OPTIMIZED: Keep recording resolution low for small files
        // CSS scaling will make display look good
        if (isChromeOnIOS) {
          constraints.video = {
            width: { exact: 320 },     // 🔧 REVERTED: 320px for iPhone Chrome (bitrate control issues)
            height: { exact: 240 },    // 🔧 REVERTED: 240px for iPhone Chrome (bitrate control issues)
            frameRate: { exact: 20 },  // Keep 20 fps for algorithm requirement
            facingMode: 'user',
          };
        } else if (isMobile) {
          // Other mobile devices - upgraded quality
          constraints.video = {
            width: { exact: 640 },     // 🎯 UPGRADED: 640px for better quality
            height: { exact: 480 },    // 🎯 UPGRADED: 480px for better quality
            frameRate: { exact: 20 },  // Keep 20 fps for algorithm
            facingMode: 'user',
          };
        } else {
          // Desktop - high quality
          constraints.video = {
            width: { exact: 640 },     // 🎯 UPGRADED: 640px for better quality
            height: { exact: 480 },    // 🎯 UPGRADED: 480px for better quality
            frameRate: { exact: 20 },  // Keep 20 fps for algorithm compatibility
          facingMode: 'user',
        };
        }
      }

      let stream = null;
      
      if (needsVideo) {
        // For video recording, use the main stream
        if (!streamRef.current) {
          streamRef.current = await navigator.mediaDevices.getUserMedia(constraints);
        }
        stream = streamRef.current;
      } else {
        // For audio recording, use microphone stream
        if (!microphoneRef.current) {
          microphoneRef.current = await navigator.mediaDevices.getUserMedia({ 
            audio: true 
          });
        }
        stream = microphoneRef.current;
      }
      
      // Check if stream is active
      if (!stream || !stream.getTracks || stream.getTracks().some(track => track.readyState === 'ended')) {
        console.log('Stream inactive, creating new one...');
        if (needsVideo) {
          streamRef.current = await navigator.mediaDevices.getUserMedia(constraints);
          stream = streamRef.current;
        } else {
          microphoneRef.current = await navigator.mediaDevices.getUserMedia(constraints);
          stream = microphoneRef.current;
        }
      }
      
      return stream;
    } catch (error) {
      console.error('Error getting stream:', error);
      throw error;
    }
  };

  const startRecording = async () => {
    console.log('🎬🎬🎬 START RECORDING CALLED 🎬🎬🎬');
    console.log('🎬🎬🎬 CURRENT STEP:', currentStep);
    console.log('🎬🎬🎬 STEP TYPE:', currentStepData?.type);
    
    try {
      // 🔧 CRITICAL FIX: Generate session ID at the start of recording session
      if (!sessionId) {
        const newSessionId = `vital_signs_${Date.now()}`;
        setSessionId(newSessionId);
        console.log('🔍 DEBUG: Generated new session ID at recording start:', newSessionId);
      }
      
      // Clean up any existing recorders first
      cleanupRecorders();
      
      // Clean up streams if switching between video and audio steps
      const isVideoStep = currentStepData.type === "video";
      const hasVideoStream = streamRef.current && streamRef.current.getVideoTracks().length > 0;
      const hasAudioStream = microphoneRef.current && microphoneRef.current.getAudioTracks().length > 0;
      
      if (isVideoStep && hasAudioStream) {
        // Switching from audio to video, clean up audio stream
        console.log('🔍 DEBUG: Switching from audio to video, cleaning up audio stream');
        if (microphoneRef.current) {
          microphoneRef.current.getTracks().forEach(track => track.stop());
          microphoneRef.current = null;
        }
      } else if (!isVideoStep && hasVideoStream) {
        // Switching from video to audio, clean up video stream
        console.log('🔍 DEBUG: Switching from video to audio, cleaning up video stream');
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      }
      
      setRecording(true);
      
      // Initialize recording time based on step type
      if (currentStepData.type === "video") {
        setRecordingTime(videoDuration); // Use the state variable instead of hardcoded 15
      } else if (currentStepData.type === "pulse_audio") {
        setRecordingTime(pulseDuration); // Use the state variable for pulse audio
      } else {
        setRecordingTime(0); // Start count-up from 0 for other audio recordings
      }

      // Get or create appropriate stream
      const needsVideo = currentStepData.type === "video";
      const stream = await getOrCreateStream(needsVideo);

      if (currentStepData.type === "video") {
        // 🎯 RECORDING FORMAT OPTIMIZATION: Use optimized video format selection
        const videoMimeType = getOptimalMimeType('video');
        const videoConfig = getRecordRTCConfig('video', videoMimeType);
        
        // 🔍 LOGGER: RecordRTC instance creation with config
        console.log('🔍🔍🔍 CREATING VIDEO RECORDER 🔍🔍🔍');
        console.log('🔍 Video MIME Type selected:', videoMimeType);
        console.log('🔍 Video RecordRTC Config:', videoConfig);
        console.log('🔍 Stream settings before recording:', stream.getTracks().map(t => ({ 
          kind: t.kind, 
          settings: t.getSettings() 
        })));
        
        const videoRecorder = new RecordRTC(stream, videoConfig);
        videoRecorderRef.current = videoRecorder;
        videoRecorder.startRecording();
        console.log('🔍 DEBUG: Video recorder started with format:', videoMimeType);
      } else {
        // 🎯 RECORDING FORMAT OPTIMIZATION: Use optimized audio format selection
        const audioMimeType = getOptimalMimeType('audio');
        const audioConfig = getRecordRTCConfig('audio', audioMimeType);
        
        const audioRecorder = new RecordRTC(stream, audioConfig);
        audioRecorderRef.current = audioRecorder;
        audioRecorder.startRecording();
        console.log('🔍 DEBUG: Audio recorder started with format:', audioMimeType);
      }
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording');
      setRecording(false);
    }
  };

  const stopRecording = async () => {
    console.log('🛑🛑🛑 STOP RECORDING CALLED 🛑🛑🛑');
    console.log('🛑🛑🛑 CURRENT STEP:', currentStep);
    console.log('🛑🛑🛑 STEPS LENGTH:', steps.length);
    console.log('🛑🛑🛑 IS LAST STEP:', currentStep >= steps.length - 1);
    
    try {
      setRecording(false);
      setUploading(true);
      
      let videoBlob = null;
      let audioBlob = null;
      
      console.log('🔍 DEBUG: Stopping recording for step:', currentStep, 'type:', currentStepData.type);
      
      // Get recorded blobs based on current step type
      if (currentStepData.type === "video" && videoRecorderRef.current) {
        console.log('🔍 DEBUG: Getting video blob...');
        videoBlob = await new Promise(resolve => {
          videoRecorderRef.current.stopRecording(() => {
            const blob = videoRecorderRef.current.getBlob();            
            resolve(blob);
          });
        });
      }
      
      if ((currentStepData.type === "pulse_audio" || currentStepData.type === "vowel_audio") && audioRecorderRef.current) {
        console.log('🔍 DEBUG: Getting audio blob...');
        audioBlob = await new Promise(resolve => {
          audioRecorderRef.current.stopRecording(() => {
            const blob = audioRecorderRef.current.getBlob();
            console.log('🔍 DEBUG: Audio blob size:', blob.size);
            resolve(blob);
          });
        });
      }

      console.log('🔍 DEBUG: Blobs created - Video:', !!videoBlob, 'Audio:', !!audioBlob);

      // Upload to storage based on current step
      if (currentStep === 0 && videoBlob) {
        // Step 1: Video Recording
        console.log('🔍 DEBUG: Uploading video recording...');
        await uploadVideoRecording(videoBlob);
      } else if (currentStep === 1 && audioBlob) {
        // Step 2: Pulse Audio
        console.log('🔍 DEBUG: Uploading pulse audio...');
        await uploadPulseAudio(audioBlob);
      } else if (currentStep === 2 && audioBlob) {
        // Step 3: Vowel Audio
        console.log('🔍 DEBUG: Uploading vowel audio...');
        await uploadVowelAudio(audioBlob);
      } else {
        console.error('🔍 DEBUG: No blob available for upload - Step:', currentStep, 'Video:', !!videoBlob, 'Audio:', !!audioBlob);
      }

      // Move to next step or complete
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
        setRecordingTime(0);
        // Only clean up recorders, keep streams active for next step
        cleanupRecorders();
      } else {
        await completeVitalSignsSession();
      }
    } catch (error) {
      console.error('Stop recording error:', error);
      toast.error('Failed to save recording');
    } finally {
      setUploading(false);
    }
  };

  // Old completeSession function removed - now using completeVitalSignsSession

  const formatTime = (seconds) => {
    return `${seconds}s`;
  };

  const formatTimerDisplay = (currentTime, stepType) => {
    if (stepType === "video") {
      // For Step 1 (Video Recording), show countdown format: "15s / 15s" -> "14s / 15s" -> "1s / 15s"
      return `${currentTime}s / ${videoDuration}s`;
    } else if (stepType === "pulse_audio") {
      // For Step 2 (Pulse Audio), show countdown format: "30s / 30s" -> "29s / 30s" -> "1s / 30s"
      return `${currentTime}s / ${pulseDuration}s`;
    } else {
      // For other steps (audio), show count-up format: "0s / 15s" -> "1s / 15s" -> "15s / 15s"
      return `${currentTime}s / ${currentStepData.duration}s`;
    }
  };

  const renderPermissionStatus = () => {
    if (permissions.camera === 'granted' && permissions.microphone === 'granted') {
      return (
        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 rounded-lg">
            <ShieldCheck className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">Permissions Granted</span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <Shield className="h-6 w-6 text-yellow-600 mr-2" />
          <h3 className="text-lg font-medium text-yellow-900">Permissions Required</h3>
        </div>
        <div className="space-y-3 mb-4">
          <div className="flex items-center">
            <Camera className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-gray-700">Camera: {permissions.camera || 'Not checked'}</span>
          </div>
          <div className="flex items-center">
            <Mic className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-gray-700">Microphone: {permissions.microphone || 'Not checked'}</span>
          </div>
        </div>
        <button
          onClick={requestPermissions}
          disabled={permissionRequested}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {permissionRequested ? 'Requesting...' : 'Grant Permissions'}
        </button>
      </div>
    );
  };

  const renderEkgWaveform = () => {
    if (currentStepData.type !== 'pulse_audio') return null;

    return (
      <div className="mt-6">
        <div className="bg-gray-900 rounded-lg p-6">
          <h4 className="text-white text-lg font-medium mb-4 text-center">
            Pulse Audio Analysis
          </h4>
          
          {/* ECG Waveform Canvas */}
          <div className="bg-black rounded-lg p-4 mb-4">
            <canvas
              ref={videoRef}
              width="400"
              height="200"
              className="w-full h-full border border-gray-600 rounded"
              style={{ 
                background: '#0a0a2a',
                filter: 'brightness(1.2) contrast(1.1)',
                border: '2px solid #4a5568',
                boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)'
              }}
            />
          </div>
          
          {/* Pulse Rate Display */}
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-red-100 rounded-lg">
              <Heart className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800 font-medium">
                Pulse Rate: {pulseRate} BPM
              </span>
            </div>
          </div>
          
          {/* Audio Level Indicator */}
          <div className="mt-4">
            <div className="bg-gray-800 rounded-full h-4 mb-2">
              <div
                className={`h-full rounded-full transition-all duration-200 ${
                  audioLevel > 100 ? 'bg-green-500' : audioLevel > 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min((audioLevel / 255) * 100, 100)}%` }}
              />
            </div>
            <p className="text-white text-sm text-center">
              Audio Level: <span className="font-mono">{Math.round(audioLevel)}</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderVowelWaveform = () => {
    if (currentStepData.type !== 'vowel_audio') return null;

    return (
      <div className="mt-6">
        <div className="bg-gray-900 rounded-lg p-6">
          <h4 className="text-white text-lg font-medium mb-4 text-center">
            Vowel Audio Waveform
          </h4>
          
          {/* Vowel Waveform Canvas */}
          <div className="bg-black rounded-lg p-4 mb-4">
            <canvas
              ref={vowelCanvasRef}
              width="400"
              height="150"
              className="w-full h-full border border-gray-600"
            />
          </div>
          
          {/* Segment Progress */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-lg">
              <Mic className="h-5 w-5 text-blue-600 " />
              <span className="text-blue-800 font-medium">
                Segment {Math.floor(recordingTime / 2) + 1} of 6
              </span>
            </div>
          </div>
          
          {/* Audio Level Indicator */}
          <div className="mt-4">
            <div className="bg-gray-800 rounded-full h-4 mb-2">
              <div
                className={`h-full rounded-full transition-all duration-200 ${
                  audioLevel > 100 ? 'bg-green-500' : audioLevel > 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min((audioLevel / 255) * 100, 100)}%` }}
              />
            </div>
            <p className="text-white text-sm text-center">
              Audio Level: <span className="font-mono">{Math.round(audioLevel)}</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderFrequencyVisualization = () => {
    // Only show frequency visualization for Steps 2 & 3 (audio steps)
    if (currentStepData.showCamera) return null;

    const getFrequencyColor = () => {
      // Use only black and white colors
      if (audioLevel > 100) return 'bg-white';
      if (audioLevel > 50) return 'bg-gray-400';
      return 'bg-gray-600';
    };

    const getFrequencyHeight = () => {
      return Math.min((audioLevel / 255) * 100, 100);
    };

    return (
      <div className="mt-6">
        <div className="bg-black rounded-lg p-6">
          <h4 className="text-white text-lg font-medium mb-4 text-center">
            {currentStepData.type === "pulse_audio" ? "Pulse Frequency" : "Voice Frequency"}
          </h4>
          
          {/* Real-time frequency bars - slower animation */}
          <div className="flex items-end justify-center space-x-1 h-32 mb-4">
            {Array.from({ length: 20 }, (_, i) => {
              const height = Math.random() * audioLevel + 10;
              return (
                <div
                  key={i}
                  className={`w-2 rounded-t transition-all duration-300 ${getFrequencyColor()}`}
                  style={{ height: `${height}%` }}
                />
              );
            })}
          </div>

          {/* Audio level indicator */}
          <div className="bg-gray-800 rounded-full h-4 mb-4">
            <div
              className={`h-full rounded-full transition-all duration-500 ${getFrequencyColor()}`}
              style={{ width: `${getFrequencyHeight()}%` }}
            />
          </div>

          {/* Frequency data */}
          <div className="text-center">
            <p className="text-white text-sm">
              Audio Level: <span className="font-mono">{Math.round(audioLevel)}</span>
            </p>
            <p className="text-gray-400 text-xs">
              {currentStepData.type === "pulse_audio" 
                ? "Detecting pulse frequency from chest placement"
                : "Analyzing voice frequency and pitch"
              }
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    console.log('🔍 DEBUG: renderResults called with results:', results);
    
    if (!results) {
      console.log('🔍 DEBUG: No results to display');
      return null;
    }

    console.log('🔍 DEBUG: Rendering results:', results);
    
    const getHeartRateStatus = () => {
      // Handle 'N/A' values
      if (results.heartRate === 'N/A' || typeof results.heartRate !== 'number') {
        return { status: 'Data unavailable', color: 'text-gray-600', bg: 'bg-gray-50' };
      }
      if (results.heartRate < 50) return { status: 'Out of range', color: 'text-red-600', bg: 'bg-red-50' };
      if (results.heartRate > 100) return { status: 'Out of range', color: 'text-red-600', bg: 'bg-red-50' };
      return { status: 'Within range', color: 'text-green-600', bg: 'bg-green-50' };
    };

    const getBPStatus = () => {
      const systolic = results.bloodPressure.systolic;
      const diastolic = results.bloodPressure.diastolic;
      
      // Handle 'N/A' values
      if (systolic === 'N/A' || diastolic === 'N/A' || 
          typeof systolic !== 'number' || typeof diastolic !== 'number') {
        return { status: 'Data unavailable', color: 'text-gray-600', bg: 'bg-gray-50' };
      }
      
      // Calculate MAP using formula: MAP = (SBP + (2*DBP)) / 3
      const map = (systolic + (2 * diastolic)) / 3;
      
      // Use MAP for range determination: within range 65-100, out of range otherwise
      if (map < 65 || map > 100) return { status: 'Out of range', color: 'text-red-600', bg: 'bg-red-50' };
      return { status: 'Within range', color: 'text-green-600', bg: 'bg-green-50' };
    };

    const getHRVStatus = () => {
      // Handle 'N/A' values
      if (results.heartRateVariability === 'N/A' || typeof results.heartRateVariability !== 'number') {
        return { status: 'Data unavailable', color: 'text-gray-600', bg: 'bg-gray-50' };
      }
      if (results.heartRateVariability < 30 || results.heartRateVariability > 100) return { status: 'Out of range', color: 'text-red-600', bg: 'bg-red-50' };
      return { status: 'Within range', color: 'text-green-600', bg: 'bg-green-50' };
    };

    const getRhythmStatus = () => {
      // Handle 'N/A' values
      if (results.rhythmRegularity === 'N/A' || !results.rhythmRegularity) {
        return { status: 'N/A', color: 'text-gray-600', bg: 'bg-gray-50' };
      }
      
      // Algorithm provides rhythm as STRING: "Regular", "Irregular", "Indeterminate"
      const rhythmStr = results.rhythmRegularity.toString().toLowerCase();
      if (rhythmStr === 'regular') {
        return { status: 'Regular', color: 'text-green-600', bg: 'bg-green-50' };
      } else if (rhythmStr === 'irregular') {
      return { status: 'Irregular', color: 'text-yellow-600', bg: 'bg-yellow-50' };
      } else if (rhythmStr === 'indeterminate') {
        return { status: 'Indeterminate', color: 'text-gray-600', bg: 'bg-gray-50' };
      }
    };

    const heartRateStatus = getHeartRateStatus();
    const bpStatus = getBPStatus();
    const hrvStatus = getHRVStatus();
    const rhythmStatus = getRhythmStatus();

    return (
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Vital Signs Analysis Complete</h2>
          <p className="text-lg text-gray-600">
            Your health data has been successfully recorded and analyzed
          </p>

        </div>

        {/* Primary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className={`text-center p-6 rounded-lg border-2 ${heartRateStatus.bg} border-gray-200`}>
            <div className="h-10 w-10 bg-green-500 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-white text-sm font-bold">HR</span>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">{typeof results.heartRate === 'number' ? Math.round(results.heartRate) : results.heartRate} BPM</p>
            <p className={`text-sm font-semibold ${heartRateStatus.color} mb-2`}>
              {heartRateStatus.status}
            </p>
            <p className="text-xs text-gray-600">Heart Rate</p>
          </div>

          <div className={`text-center p-6 rounded-lg border-2 ${bpStatus.bg} border-gray-200`}>
            <div className="h-10 w-10 bg-green-500 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-white text-sm font-bold">BP</span>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">
              {typeof results.bloodPressure.systolic === 'number' ? Math.round(results.bloodPressure.systolic) :results.bloodPressure.systolic}/{typeof results.bloodPressure.diastolic === 'number' ? Math.round(results.bloodPressure.diastolic) : results.bloodPressure.diastolic} mmHg
              {/* 🔧 BP FIX: Add hidden element to trigger re-render */}
              <span style={{display: 'none'}}>{forceUpdate}</span>
            </p>
            
            <p className={`text-xs mb-3 font-semibold ${bpStatus.color}`}>
              {bpStatus.status}
            </p>
            <p className="text-xs text-gray-600 ">Blood Pressure</p>
          </div>

          <div className={`text-center p-6 rounded-lg border-2 ${hrvStatus.bg} border-gray-200`}>
          <div className="h-10 w-10 bg-green-500 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-white text-sm font-bold">HRV</span>
            </div>
            <p className="text-xl font-bold text-gray-900 mb-1">{typeof results.heartRateVariability === 'number' ? Math.round(results.heartRateVariability) : results.heartRateVariability} ms</p>
            <p className={`text-sm font-semibold ${hrvStatus.color} mb-2`}>
              {hrvStatus.status}
            </p>
            <p className="text-xs text-gray-600">Heart Rate Variability</p>
          </div>

          <div className={`text-center p-6 rounded-lg border-2 ${rhythmStatus.bg} border-gray-200`}>
          <div className="h-10 w-10 bg-green-500 rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-white text-sm font-bold">RR</span>
            </div>
            <p className={`text-lg font-bold ${rhythmStatus} mb-2`}>
              {rhythmStatus.status}
            </p>
            <p className="text-xs text-gray-600">Rhythm Regularity</p>
          </div>
        </div>



        <div className="text-center">
          <div className="inline-flex items-center px-6 py-3 bg-green-100 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">Analysis Complete</span>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Healthcare Provider will reach out to you shortly
          </p>
        </div>
      </div>
    );
  };

  if (sessionComplete) {
    console.log('🔍 DEBUG: Session complete, rendering results. Results state:', results);
    return ( 
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-4 px-4">
        {renderResults()}
      </div>
    );
  }

  // Special full-page layout for voice capture step
  if (currentStepData.type === 'vowel_audio') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Responsive Header */}
          <div className="text-center py-2 px-2 sm:px-4">
            {/* Compact Progress Steps */}
            <div className="flex justify-center mb-2">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                    index <= currentStep ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    {index < currentStep ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <step.icon className="h-3 w-3" />
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-1 ${
                      index < currentStep ? 'bg-blue-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Voice Capture Content */}
                     <div className="bg-white rounded-lg shadow-lg p-2 md:p-3 flex flex-col mx-auto w-full relative w-full md:w-auto md:max-w-[400px] md:min-w-[280px] max-h-[95vh] overflow-y-auto">
            {/* Help Tooltip - Top Right Corner (hidden for step 3) */}
            {/* <div className="absolute top-2 right-2 z-10">
                <HelpTooltip currentStep={currentStep} />
            </div> */}
            
            {/* Instructions at top */}
            <div className="text-center mb-2 md:mb-2">
              <div className="flex items-center justify-center mb-2">
                <Info className="h-5 w-5 text-blue-600 mr-2" />
                <p className="text-sm text-gray-700 mt-6">
                  Hold your phone in front of your mouth and record 15 seconds of speech.
                </p>
              </div>
              </div>
              
            {/* Person with phone illustration */}
            <div className="flex justify-center mb-2 md:mb-1">
              <div className="w-full h-[50vh] md:w-[300px] md:h-[120px]">
                <img 
                  src="/Media.jpg" 
                  alt="Person holding phone for voice recording"
                  className="w-full h-full object-cover rounded-lg"
                  style={{ 
                    width: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
             </div>

            {/* Minimal Vector-Style Waveform Design */}
            <div className="flex justify-center mb-2">
              {uploading ? (
                // Loading State - Show saving text in place of waveform
                <div className="flex items-center justify-center w-32 h-32">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-blue-600 font-medium">Saving...</p>
             </div>
                </div>
              ) : (
                <div className="relative w-32 h-32">
                  {/* Central solid circle - responds to audio */}
                  <div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-200"
                    style={{
                      width: recording && audioLevel > 15 ? `${4 + (audioLevel / 255) * 8}px` : '16px',
                      height: recording && audioLevel > 15 ? `${4 + (audioLevel / 255) * 8}px` : '16px',
                      opacity: recording && audioLevel > 15 ? 1 : 0.3,
                      backgroundColor: recording && audioLevel > 15 ? '#3b82f6' : '#9ca3af' // Blue when sound, gray when silent
                    }}
                  ></div>
                  
                  
                  {/* Concentric arcs radiating outward symmetrically on all sides */}
                  {Array.from({ length: 6 }, (_, i) => {
                    const size = 20 + (i * 16); // Increasing sizes: 36px, 52px, 68px, 84px, 100px, 116px
                    const baseOpacity = 0.9 - (i * 0.12); // Base opacity: 0.9, 0.78, 0.66, 0.54, 0.42, 0.3
                    
                    // Real-time animation based on audio level - only animate when there's sound
                    const hasAudio = recording && audioLevel > 15; // Threshold for detecting sound
                    const audioInfluence = hasAudio ? (audioLevel / 255) * 0.4 : 0;
                    const dynamicOpacity = hasAudio ? Math.min(baseOpacity + audioInfluence, 1) : 0.1; // Very low opacity when no sound
                    const pulseScale = hasAudio ? 1 + (audioLevel / 255) * 0.3 : 1; // No scaling when no sound
                    
                    return (
                      <div key={i} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        {/* Left side arc */}
                        <div 
                          className="absolute border-2 border-blue-500 rounded-full transition-all duration-300 ease-out"
                  style={{ 
                            width: `${size * pulseScale}px`,
                            height: `${size * pulseScale}px`,
                            left: `-${size * pulseScale / 2}px`,
                            top: `-${size * pulseScale / 2}px`,
                            borderRight: 'none',
                            borderTop: 'none',
                            borderBottom: 'none',
                            opacity: dynamicOpacity,
                            transform: 'rotate(-45deg)',
                            animation: hasAudio ? `pulseArc ${1.5 + i * 0.2}s infinite ${i * 0.1}s` : 'none'
                          }}
                        ></div>
                        
                        {/* Right side arc */}
                        <div 
                          className="absolute border-2 border-blue-500 rounded-full transition-all duration-300 ease-out"
                          style={{
                            width: `${size * pulseScale}px`,
                            height: `${size * pulseScale}px`,
                            left: `-${size * pulseScale / 2}px`,
                            top: `-${size * pulseScale / 2}px`,
                            borderLeft: 'none',
                            borderTop: 'none',
                            borderBottom: 'none',
                            opacity: dynamicOpacity,
                            transform: 'rotate(45deg)',
                            animation: hasAudio ? `pulseArc ${1.5 + i * 0.2}s infinite ${i * 0.1}s` : 'none'
                          }}
                        ></div>

                        {/* Bottom arc */}
                        <div 
                          className="absolute border-2 border-blue-500 rounded-full transition-all duration-300 ease-out"
                          style={{
                            width: `${size * pulseScale}px`,
                            height: `${size * pulseScale}px`,
                            left: `-${size * pulseScale / 2}px`,
                            top: `-${size * pulseScale / 2}px`,
                            borderLeft: 'none',
                            borderTop: 'none',
                            borderRight: 'none',
                            opacity: dynamicOpacity,
                            transform: 'rotate(90deg)',
                            animation: hasAudio ? `pulseArc ${1.5 + i * 0.2}s infinite ${i * 0.1}s` : 'none'
                          }}
                        ></div>

                        {/* Top arc - IDENTICAL to bottom arc, just rotated upward */}
                        <div 
                          className="absolute border-2 border-blue-500 rounded-full transition-all duration-300 ease-out"
                          style={{
                            width: `${size * pulseScale}px`,
                            height: `${size * pulseScale}px`,
                            left: `-${size * pulseScale / 2}px`,
                            top: `-${size * pulseScale / 2}px`,
                            borderLeft: 'none',
                            borderTop: 'none',
                            borderRight: 'none',
                            opacity: dynamicOpacity,
                            transform: 'rotate(270deg)',
                            animation: hasAudio ? `pulseArc ${1.5 + i * 0.2}s infinite ${i * 0.1}s` : 'none'
                          }}
                        ></div>
                      </div>
                    );
                  })}
                </div>
              )}
                </div>
                
            {/* CSS for real-time arc animations */}
            <style jsx="true">{`
              @keyframes pulseArc {
                0% {
                  transform: scale(1);
                  opacity: 0.3;
                }
                25% {
                  transform: scale(1.05);
                  opacity: 0.6;
                }
                50% {
                  transform: scale(1.1);
                  opacity: 0.8;
                }
                75% {
                  transform: scale(1.05);
                  opacity: 0.6;
                }
                100% {
                  transform: scale(1);
                  opacity: 0.3;
                }
              }
            `}</style>

            {/* Recording Controls */}
            <div className="text-center mt-2">
              {!recording ? (
                <button
                  onClick={startRecording}
                  className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors flex items-center mx-auto text-base"
                >
                  <div className="w-6 h-6 border border-white rounded-full mr-2 flex items-center justify-center">
                      </div>
                  Start Recording
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="text-base font-semibold text-gray-700">
                    {recordingTime}s remaining
                  </div>
                  <button
                    onClick={stopRecording}
                    className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors flex items-center mx-auto text-base"
                  >
                    <div className="w-6 h-6 border border-white rounded-full mr-2 flex items-center justify-center">
                    </div>
                    
                    Stop Recording
                  </button>
                  </div>
                )}
            </div>



            {/* Privacy notice */}
            <div className="mt-2 mb-2 text-center">
              <p className="text-sm text-gray-600 mb-2">
                Your voice is securely encrypted and only used for biomarker analysis.
              </p>
                    </div>
                  </div>
        </div>
      </div>
    );
  }

  // Special full-page layout for pulse audio step
  if (currentStepData.type === 'pulse_audio') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Responsive Header */}
          <div className="text-center py-2 px-2 sm:px-4">
            {/* Compact Progress Steps */}
            <div className="flex justify-center mb-2">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                    index <= currentStep ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    {index < currentStep ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <step.icon className="h-3 w-3" />
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-1 ${
                      index < currentStep ? 'bg-blue-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

                                                               {/* Pulse Audio Content */}
            <div className="bg-white rounded-lg shadow-lg p-2 md:p-3 flex flex-col mx-auto w-full relative w-full md:w-auto md:max-w-[400px] md:min-w-[280px] max-h-[95vh] overflow-y-auto">
            {/* Help Tooltip - Top Right Corner (hidden for step 2) */}
            {/* <div className="absolute top-2 right-2 z-10">
              <HelpTooltip currentStep={currentStep} />
            </div> */}
            
            {/* Instructions at top */}
            <div className="text-center mb-2 md:mb-2">
              <div className="flex items-start justify-center mb-1">
                <Info className="h-5 w-5 text-blue-600 mr-0.5  flex-shrink-0" />
                <p className="text-sm text-gray-700 leading-tight">
                  Place your phone's microphone ~2 inches from your chest or throat. Stay still and quiet during capture.
                </p>
              </div>
            </div>

            {/* Person with phone illustration with arrow navigation */}
            <div className="flex justify-center mb-2 md:mb-2 w-full">
                                         {/* Image Container with Overlay Arrows - Responsive dimensions using Tailwind */}
                                                                                                               <div className="relative w-full h-[55vh] md:w-[300px] md:h-[200px]">
                                  <img 
                    src={currentImageIndex === 0 ? "/Media1.jpg" : "/Media2.jpg"} 
                    alt={currentImageIndex === 0 ? "Person holding phone to chest" : "Person holding phone to throat"}
                    className="w-full h-full object-cover rounded-lg"
                    style={{ 
                      width: '100%',
                      objectFit: 'cover'
                    }}
                  />
                
                {/* Left Arrow - Overlay */}
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === 0 ? 1 : 0)}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 hover:bg-opacity-100 p-2 rounded-full shadow-md transition-all"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-700" />
                </button>
                
                {/* Right Arrow - Overlay */}
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === 0 ? 1 : 0)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 hover:bg-opacity-100 p-2 rounded-full shadow-md transition-all"
                >
                  <ChevronRight className="h-5 w-5 text-gray-700" />
                </button>
              </div>
            </div>
            
            {/* Image indicator dots */}
            <div className="flex justify-center mb-2 md:mb-2">
              <div className="flex space-x-2">
                <div className={`w-2 h-2 rounded-full ${currentImageIndex === 0 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`w-2 h-2 rounded-full ${currentImageIndex === 1 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
              </div>
            </div>

            {/* Waveform visualization */}
            <div className="relative w-full mb-2 md:mb-2" style={{ 
              minHeight: '50px', 
              maxHeight: '60px'
            }}>
              <div className="w-full h-full flex items-center justify-center p-2">
                {uploading ? (
                  // Loading State - Replace waveform with loading logo and text
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-blue-600 font-medium">Saving...</p>
                  </div>
                ) : (
                  // Normal Waveform
                  <AnimatedZigzagWaveform 
                    isRecording={recording} 
                    audioLevel={audioLevel}
                    containerHeight={60}
                    color="rgb(59, 130, 246)"
                    speed={1.5}
                    barCount={40}
                  />
                )}
              </div>
              </div>

              {/* Recording Controls */}
            <div className="text-center">
              {!recording ? (
                <button
                  onClick={startRecording}
                  className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors flex items-center mx-auto text-lg"
                >
                  <div className="w-8 h-8 border border-white rounded-full mr-1 -ml-6 flex items-center justify-center">
                  </div>
                  Start Recording
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="text-lg font-semibold text-gray-700">
                    {recordingTime}s remaining
                  </div>
                  {uploading ? (
                    // Loading State - Show loading logo and saving text
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-sm text-blue-600 font-medium">Saving...</p>
                    </div>
                  ) : (
                    // Stop Recording Button
                  <button
                    onClick={stopRecording}
                      className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors flex items-center mx-auto text-lg"
                  >
                      <div className="w-8 h-8 border border-white rounded-full mr-1 -ml-6 flex items-center justify-center">
                      </div>
                    Stop Recording
                  </button>
                  )}
                </div>
              )}
            </div>



            {/* Privacy notice */}
            <div className="mt-2 text-center">
              <p className="text-sm text-gray-600 mb-2">
                Your audio is securely encrypted and only used for biomarker analysis.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 px-1 sm:px-4 py-1 overflow-hidden">
             <div className="w-full mx-auto h-full flex flex-col max-w-full md:max-w-4xl">
        {/* Responsive Header (removed title/subtitle as requested) */}
        <div className="text-center mb-1 md:mb-1"></div>

        {/* Permission Status - Moved to camera overlay */}

        {/* Compact Progress Steps */}
        <div className="flex justify-center mb-2 md:mb-1">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                index <= currentStep ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-400'
              }`}>
                {index < currentStep ? (
                  <CheckCircle className="h-3 w-3" />
                ) : (
                  <step.icon className="h-3 w-3" />
                )}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${
                  index < currentStep ? 'bg-blue-500' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Current Step - Responsive Layout */}
                 <div className="bg-white rounded-lg shadow-lg p-0.5 md:p-1 flex flex-col mx-auto w-full relative w-full md:w-auto md:max-w-[400px] md:min-w-[280px] max-h-[98vh] overflow-y-auto">
          {/* Help Tooltip - Top Right Corner (hidden for step 1) */}
          {!currentStepData.showCamera && (
          <div className="absolute top-2 right-2 z-10">
            <HelpTooltip currentStep={currentStep} />
          </div>
          )}
          
          {/* Text heading removed for step 1 */}
          {!currentStepData.showCamera && (
          <div className="text-center mb-1">
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900 mb-0.5">
               {currentStepData.title}
            </h2>
            
            {/* Ultra Compact Recording Tips - Only for Pulse Audio */}
            {currentStepData.type === 'pulse_audio' && (
              <div className="mb-1">
                <ul className="text-center text-blue-800 space-y-0 text-xs">
                  {currentStepData.tips.slice(0, 1).map((tip, index) => (
                    <li key={index} className="flex items-start justify-center">
                      <span className="text-blue-500 mr-1">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          )}

                                  {/* Instructions at top - Outside camera screen */}
              {currentStepData.showCamera && (
                <div className="text-center mb-1 md:mb-2">
                  <div className="flex items-start justify-center">
                    <Info className="h-4 w-4 text-blue-600 mr-1 flex-shrink-0 mt-0" />
                    <p className="text-xs text-gray-700 leading-tight">
                      Hold your phone at eye level, centered, and record a 30-second video of yourself.
                    </p>
                  </div>
                </div>
              )}

                      {/* Step 1: Camera Section - Responsive Video Recording */}
            {currentStepData.showCamera && (
                               <div className="relative bg-black rounded-lg overflow-hidden mb-0 md:mb-0 mx-auto w-full h-[80vh] md:h-[500px] md:max-w-[400px]">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ 
                  transform: 'scaleX(-1)',  // Keep mirror effect
                  // 🎯 ADVANCED CSS: Make 320x240 stream look sharp when scaled up
                  width: '100%',           // Fill container width
                  height: '100%',          // Fill container height
                  objectFit: 'cover',      // Smooth scaling without distortion
                  imageRendering: 'optimizeQuality',  // Better scaling quality
                }}
                onError={(e) => {
                  console.error('🔍 DEBUG: Video element error:', e);
                  setCameraError('Video playback error. Please refresh the page.');
                }}
                onLoadStart={() => console.log('🔍 DEBUG: Video loading started')}
                onLoadedData={() => console.log('🔍 DEBUG: Video data loaded')}
                onCanPlay={() => console.log('🔍 DEBUG: Video can play')}
              />
              
              {/* Face Detection Oval with Start Button Inside */}
              {cameraReady && !cameraError && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {/* Face Detection Oval */}
                <FaceDetectionOval 
                  showInstructions={false}
                    size="large"
                  orientation="vertical"
                  showCornerGuides={false}
                  showCenterCrosshair={false}
                  videoRef={videoRef}
                  recording={recording}
                  recordingTime={recordingTime}
                />
                  
                  {/* Start Button at Bottom Center */}
                  {!recording && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-auto">
                      <button
                        onClick={startRecording}
                        className="p-4 rounded-full transition-all duration-200 flex items-center justify-center"
                      >
                        <div className="w-10 h-10 bg-red-600 border-4 border-white-700 rounded-full"></div>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Countdown Timer - Upper Right Corner */}
              {recording && cameraReady && !cameraError && (
                <div className="absolute top-1 right-1">
                  <div className="bg-black bg-opacity-70 text-white px-1 py-0.5 rounded-full text-xs font-medium">
                    {formatTime(recordingTime)}
                  </div>
                </div>
              )}

              {/* Stop Recording Button - Bottom Center when recording */}
              {recording && cameraReady && !cameraError && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-auto">
                  <button
                    onClick={stopRecording}
                    className="p-4 rounded-full transition-all duration-200 flex items-center justify-center"
                  >
                    <div className="w-10 h-10 border-2 border-white-600 rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 bg-red-600 rounded-sm"></div>
                    </div>
                  </button>
                </div>
              )}

              {/* Loading Overlay - Full screen when uploading */}
              {uploading && cameraReady && !cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 z-30">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3"></div>
                    <p className="text-lg text-white font-medium">Saving...</p>
                  </div>
                </div>
              )}

              {/* Permission Overlay - Show when permissions not granted */}
              {currentStepData.showCamera && (permissions.camera !== 'granted' || permissions.microphone !== 'granted') && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50">
                  <div className="text-center p-6 max-w-sm mx-4">
                    <Shield className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-3">Permissions Required</h3>
                    <p className="text-sm text-gray-300 mb-4">
                      We need access to your camera and microphone to record your vital signs.
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-center text-sm text-gray-300">
                        <Camera className="h-4 w-4 text-gray-400 mr-2" />
                        <span>Camera: {permissions.camera || 'Not checked'}</span>
                      </div>
                      <div className="flex items-center justify-center text-sm text-gray-300">
                        <Mic className="h-4 w-4 text-gray-400 mr-2" />
                        <span>Microphone: {permissions.microphone || 'Not checked'}</span>
                      </div>
                    </div>
                    <button
                      onClick={requestPermissions}
                      disabled={permissionRequested}
                      className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {permissionRequested ? 'Requesting...' : 'Grant Permissions'}
                    </button>
                  </div>
                </div>
              )}

              {/* Permission Success Overlay - Brief confirmation when granted */}
              {currentStepData.showCamera && permissions.camera === 'granted' && permissions.microphone === 'granted' && !cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-green-900 bg-opacity-80 z-50">
                  <div className="text-center p-6">
                    <ShieldCheck className="h-12 w-12 text-green-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">Permissions Granted!</h3>
                    <p className="text-sm text-gray-300">Initializing camera...</p>
                  </div>
                </div>
              )}

              {/* Overlay states */}
              {cameraError ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
                  <div className="text-center p-4">
                    <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-3" />
                    <p className="text-sm font-medium mb-2">Camera Access Failed</p>
                    <p className="text-xs text-gray-300 mb-3 px-4">{cameraError}</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => initializeCamera()}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors mr-2"
                      >
                        Try Again
                      </button>
                      <button
                        onClick={() => requestPermissions()}
                        className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition-colors"
                      >
                        Grant Permissions
                      </button>
                    </div>
                  </div>
                </div>
              ) : !cameraReady ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-1"></div>
                    <p className="text-xs font-medium">Initializing Camera...</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Pulse Audio is now handled in full-page layout above */}

          {/* Vowel Audio - Direct Waveform Area */}
          {currentStepData.type === 'vowel_audio' && (
            <div className="flex-1 flex flex-col justify-center items-center">
              <div className="relative w-full" style={{ 
                minHeight: 'clamp(140px, 15vh, 160px)', 
                maxHeight: 'clamp(150px, 18vh, 180px)', 
                maxWidth: '100%'
              }}>
                {/* Real-time Frequency Analyzer */}
                <div className="w-full h-full flex items-center justify-center">
                  <RealTimeAudioVisualizer 
                    isRecording={recording} 
                    barCount={28} 
                    barWidth={4} 
                    barGap={3}
                    containerHeight={128}
                  />
                </div>
                
                {/* Countdown Timer - Adjusted Position */}
                {recording && (
                  <div className="absolute top-1 right-1">
                    <div className="bg-black bg-opacity-80 text-white px-2 py-0.5 rounded-full text-sm font-medium">
                      {recordingTime}s
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Duration Display - Compact (removed for step 1) */}
          {!currentStepData.showCamera && (
            <div className="flex justify-center items-center mt-1 mb-1">
              <span className="text-xs text-gray-600">Duration: {currentStepData.duration}s</span>
            </div>
          )}

          {/* Vowel Audio Duration Display */}
          {currentStepData.type === 'vowel_audio' && (
            <div className="flex justify-center items-center mt-0 mb-0 sm:mb-1">
              <span className="text-xs text-gray-600">Duration: 15s</span>
            </div>
          )}

          {/* Recording Controls - Only for non-camera steps */}
          {!currentStepData.showCamera && (
          <div className="text-center mt-auto">
            {/* Upload Status */}
            {uploading && (
              <div className="flex items-center justify-center mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm text-blue-600">Saving recording...</span>
              </div>
            )}

            {!recording ? (
              <button
                onClick={startRecording}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors flex items-center mx-auto text-sm"
              >
                <Play className="mr-1 h-4 w-4" />
                Start Recording
              </button>
            ) : (
              <div className="flex justify-center space-x-4">
              <button
                onClick={stopRecording}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition-colors flex items-center text-sm"
              >
                  <Square className="mr-1 h-4 w-4" />
                Stop Recording
              </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Processing Status Overlay */}
      {processingStage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <h5 className="text-lg font-semibold text-gray-800 mb-2">{processingStage}</h5>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out" 
                  style={{width: `${pollingProgress}%`}}
                ></div>
              </div>
              {pollingProgress > 0 && (
                <p className="text-sm text-gray-600 mb-2">{pollingProgress}% complete</p>
              )}
              <small className="text-xs text-gray-500 block">
                This may take up to {isMobileDevice ? '4' : '3'} minutes on {isMobileDevice ? 'mobile devices' : 'slower connections'}
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Test API Button - Only show when session is complete */}
      {sessionComplete && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">🧪 Test Real API</h3>
          <p className="text-sm text-gray-600 mb-3">
            Test the real vital signs analysis API with your existing recordings
          </p>
          <div className="flex flex-wrap gap-2">
            {/* 🔧 REMOVED: Test Algorithms button - endpoint is deprecated and returns 404 */}
            {/* Button removed to prevent confusion for users */}
            
            <button
              onClick={async () => {
                if (!sessionId) {
                  toast.error('No session ID available');
                  return;
                }
                try {
                  toast.promise(
                    vitalSignsAnalysisAPI.analyzeExistingSession(sessionId),
                    {
                      loading: 'Analyzing existing session...',
                      success: (results) => {
                        console.log('🔍 Frontend Component: API call successful, raw results:', results);
                        
                        // Debug: Check if vital_signs exists
                        if (results.vital_signs) {
                          console.log('   ✅ vital_signs found in results');
                          console.log('   🔍 vital_signs keys:', Object.keys(results.vital_signs));
                          console.log('   🔍 vital_signs values:', results.vital_signs);
                          console.log('   💓 hrv value:', results.vital_signs.hrv, 'type:', typeof results.vital_signs.hrv);
                          console.log('   📈 rhythm_regularity value:', results.vital_signs.rhythm_regularity, 'type:', typeof results.vital_signs.rhythm_regularity);
                        } else {
                          console.log('   ❌ vital_signs NOT found in results');
                          console.log('   🔍 Available keys:', Object.keys(results));
                        }
                        
                        const convertedResults = vitalSignsAnalysisAPI.convertAPIResponseToResults(results);
                        console.log('   🔄 Conversion completed, converted results:', convertedResults);
                        console.log('   💓 Converted HRV:', convertedResults.heartRateVariability);
                        console.log('   📈 Converted Rhythm:', convertedResults.rhythmRegularity);
                        
                        setResults(convertedResults);
                        console.log('   ✅ Results state updated with converted data');
                        
                        return 'Session analysis successful!';
                      },
                      error: 'Session analysis failed'
                    }
                  );
                } catch (error) {
                  console.error('Analysis failed:', error);
                  toast.error('Analysis failed');
                }
              }}
              className="bg-blue-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              📊 Analyze Session
            </button>

            <button
              onClick={async () => {
                try {
                  const health = await vitalSignsAnalysisAPI.healthCheck();
                  toast.success(`API Health: ${health.status}`);
                  console.log('API Health:', health);
                } catch (error) {
                  toast.error('Health check failed');
                  console.error('Health check failed:', error);
                }
              }}
              className="bg-purple-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              ❤️ Health Check
            </button>
            
            {/* New: Manual API Test Button */}
            <button
              onClick={async () => {
                try {
                  console.log('🔍 DEBUG: Manual API test button clicked');
                  console.log('🔍 DEBUG: Testing direct fetch to backend...');
                  
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/vital-signs-analysis-simple/health`);
                  console.log('🔍 DEBUG: Direct fetch response status:', response.status);
                  
                  if (response.ok) {
                    const data = await response.json();
                    console.log('🔍 DEBUG: Direct fetch response data:', data);
                    toast.success('Direct fetch successful - check console');
                  } else {
                    console.log('🔍 DEBUG: Direct fetch failed with status:', response.status);
                    toast.error('Direct fetch failed');
                  }
                } catch (error) {
                  console.error('🔍 DEBUG: Direct fetch error:', error);
                  toast.error('Direct fetch error');
                }
              }}
              className="bg-orange-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-700 transition-colors"
            >
              🔍 Direct Fetch Test
            </button>
            
            {/* New: Direct API Test Button */}
            <button
              onClick={testDirectAPI}
              className="bg-red-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-red-700 transition-colors"
            >
              🧪 Direct API Test
            </button>
            
            {/* 🎯 RECORDING FORMAT OPTIMIZATION: Format Capabilities Test */}
            <button
              onClick={() => {
                const caps = detectFormatCapabilities();
                toast.success(`Device supports ${caps.video.length} video & ${caps.audio.length} audio formats - check console`);
              }}
              className="bg-indigo-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              📱 Format Capabilities
            </button>
          </div>
          
          {results && (
            <div className="mt-3 p-3 bg-white rounded border">
              <h4 className="font-medium text-gray-800 mb-2">Current Results:</h4>
              <div className="text-sm text-gray-600">
                <p>Heart Rate: {typeof results.heartRate === 'number' ? Math.round(results.heartRate) : results.heartRate} BPM</p>
                <p>Blood Pressure: {typeof results.bloodPressure.systolic === 'number' ? Math.round(results.bloodPressure.systolic) : results.bloodPressure.systolic}/{typeof results.bloodPressure.diastolic === 'number' ? Math.round(results.bloodPressure.diastolic) : results.bloodPressure.diastolic} mmHg
                  {/* 🔧 BP FIX: Add hidden element to trigger re-render */}
                  <span style={{display: 'none'}}>{forceUpdate}</span>
                </p>
                <p>HRV: {typeof results.heartRateVariability === 'number' ? Math.round(results.heartRateVariability) : results.heartRateVariability} ms</p>
                <p>Rhythm: {typeof results.rhythmRegularity === 'number' ? Math.round(results.rhythmRegularity) : results.rhythmRegularity}</p>
                <p>Quality: {typeof results.audioQuality === 'number' ? Math.round(results.audioQuality) : results.audioQuality}</p>
                <p>Confidence: {typeof results.confidence === 'number' ? Math.round(results.confidence) : results.confidence}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VitalSignsRecording; 